import React, { useMemo, useEffect, useState, useRef, useCallback } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
  type SortingState,
  type ColumnFiltersState,
  type PaginationState,
  type ColumnOrderState,
  type RowSelectionState,
} from '@tanstack/react-table';

// Extend ColumnMeta to include tooltip
declare module '@tanstack/react-table' {
  interface ColumnMeta<TData, TValue> {
    tooltip?: string;
  }
}
import { settingsService } from '../services/SettingsService';
import { FolderService } from '../services/FolderService';
import { authenticatedFetch } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

interface Book {
  isbn: string;
  book_title: string;
  publisher?: string;
  series?: string;
  in_all_books?: boolean;
  in_db?: boolean;
  page_count: number;
  fast_track: boolean;
  images_created: boolean;
  text_extracted_basic: boolean;
  text_extracted_blocked: boolean;
  text_extracted_enhanced: boolean;
  text_extracted?: string | boolean; // Optional - "basic", "blocked", "enhanced", or false
  text_extracted_combined?: boolean; // Optional
  text_extracted_files_prepared?: boolean; // Optional
  files_prepared_pages_processed?: number; // Optional
  possible_issues?: string; // Optional
  'ai-confidence'?: number; // Optional
  extracted_from?: string; // Optional - "pdf", etc.
  format?: string; // Optional - from all-books (e.g. "fixed")
  audio_inside?: boolean; // Optional - from all-books
  include_list_consumer?: boolean | null; // Derived on backend from lists/*.txt (null => list missing)
  include_list_educator?: boolean | null; // Derived on backend from lists/*.txt (null => list missing)
  good_audio?: boolean | null; // Derived on backend from lists/good-audio.txt (null => list missing)
  approval?: {
    images?: boolean | string;
    words?: boolean | string;
    voice?: boolean | string;
  };
  // TTS metadata from spine.json / all-books
  voice_service?: string;
  audio_service?: string;
  voice_name?: string;
  tts_chars?: number;
  tts_cost_azure?: number;
  tts_cost_elevenlabs?: number;
  changed_by?: string[] | null;
  last_changed?: string | null;
  copyright?: boolean | null;
  file_type?: string;
  audio_included?: string;
  audio_ai_ready?: string;
  quiz_question_count?: string;
  production_file_sync_status?: 'not_synced' | 'syncing' | 'synced' | 'failed';
}

interface BookTableProps {
  books: Book[];
  onOpenBook: (isbn: string) => void;
  onFiltersChange?: (hasFilters: boolean) => void;
  onPaginationChange?: (pageIndex: number) => void;
  onColumnOrderChange?: (hasCustomOrder: boolean) => void;
  onFilteredSortedStateChange?: (books: Book[], pageIndex: number, pageSize: number) => void;
  clearFiltersRef?: React.MutableRefObject<(() => void) | null>;
  currentFolder?: string;
  /** Called when sync completes for ISBNs; parent should update in-memory approval.images/words to 'auto' */
  onSyncCompleted?: (updates: Array<{ isbn: string; approval: { images?: string; words?: string } }>) => void;
}

const columnHelper = createColumnHelper<Book>();

/** DB Status column value: In Sync, Out of Sync, or Not ready (used for cell and filter). */
function getDbSyncStatus(book: Book): 'In Sync' | 'Out of Sync' | 'Not ready' {
  const approval = book.approval;
  const imagesApproved = approval?.images === true || approval?.images === 'true' || approval?.images === 'auto';
  const wordsApproved = approval?.words === true || approval?.words === 'true' || approval?.words === 'auto';
  const dbFileType = (book.file_type || '').toUpperCase();
  const dbAudioAiReady = book.audio_ai_ready;
  const audioAiReadyIsOne = String(dbAudioAiReady) === '1';

  let check1: boolean | null = null;
  if (imagesApproved) check1 = dbFileType === 'IMG';

  let check2: boolean | null = null;
  if (wordsApproved && audioAiReadyIsOne) check2 = true;
  else if (wordsApproved && !audioAiReadyIsOne) check2 = false;
  else if (!wordsApproved && audioAiReadyIsOne) check2 = false;

  if (check1 === false || check2 === false) return 'Out of Sync';
  if (check1 === true && (check2 === true || check2 === null)) return 'In Sync';
  return 'Not ready';
}

/** When false, per-row Sync and DB Sync (x) do not require images to be approved. */
const requireImageApproval = false;

/** Same criteria as Sync column: in all-books, images created, and (if requireImageApproval) images approved (true or auto). */
function isBookEligibleForDbSync(book: Book): boolean {
  const inAllBooks = book.in_all_books !== false;
  const imagesCreated = book.images_created === true;
  const approval = book.approval;
  const imagesApproved = approval?.images === true || approval?.images === 'true' || approval?.images === 'auto';
  return Boolean(inAllBooks && imagesCreated && (!requireImageApproval || imagesApproved));
}

function buildDbSyncPayload(books: Book[]): {
  payload: { updates: Array<{ id: string; fields: Record<string, unknown> }> };
  ineligibleList: Array<{ isbn: string; title?: string }>;
} {
  const eligible = books.filter(isBookEligibleForDbSync);
  const ineligible = books.filter((b) => !isBookEligibleForDbSync(b));
  const ineligibleList = ineligible.map((b) => ({ isbn: b.isbn, title: b.book_title }));
  const payload = {
    updates: eligible.map((b) => ({
      id: b.isbn,
      fields: {
        File_Type: 'IMG',
        Audio_Ai_Ready: (b.approval?.words === true || b.approval?.words === 'true' || b.approval?.words === 'auto') ? 1 : 0,
      },
    })),
  };
  return { payload, ineligibleList };
}

// Helper function to determine confidence class based on value
const getConfidenceClass = (confidence: any): string => {
  if (!confidence || confidence === 'N/A') {
    return 'unknown';
  }
  
  const numConfidence = typeof confidence === 'string' ? parseFloat(confidence) : confidence;
  
  if (isNaN(numConfidence)) {
    return 'unknown';
  }
  
  if (numConfidence >= 95) {
    return 'excellent';
  } else if (numConfidence >= 85) {
    return 'good';
  } else if (numConfidence >= 70) {
    return 'fair';
  } else {
    return 'poor';
  }
};

const BookTable: React.FC<BookTableProps> = ({ books, onOpenBook, onFiltersChange, onPaginationChange, onColumnOrderChange, onFilteredSortedStateChange, clearFiltersRef, currentFolder, onSyncCompleted }) => {
  const { accessLevel } = useAuth();
  const isKriyadocsUser = accessLevel === 'KRIYADOCS_USER';
  const isFullAccessUser = accessLevel === 'FULL_ACCESS';
  // Sync column: same rule as Sync(x) button - local OR (server AND folder is books). Kriyadocs users still don't see column.
  const [folder, setFolder] = useState<string | null>(() => FolderService.getFolderIfSet());
  const [isS3Mode, setIsS3Mode] = useState(false);
  useEffect(() => {
    const handler = () => setFolder(FolderService.getFolderIfSet());
    window.addEventListener('folderChanged', handler);
    const initHandler = () => setFolder(FolderService.getFolderIfSet());
    window.addEventListener('app-initialization-complete', initHandler);
    return () => {
      window.removeEventListener('folderChanged', handler);
      window.removeEventListener('app-initialization-complete', initHandler);
    };
  }, []);
  useEffect(() => {
    const detectS3Mode = async () => {
      try {
        const response = await authenticatedFetch('/api/books/');
        if (response.ok) {
          const config = await response.json();
          const detected = config.storageMode === 's3';
          setIsS3Mode(detected);
          setFolder(FolderService.getFolderIfSet());
        } else {
          setIsS3Mode(false);
        }
      } catch {
        setIsS3Mode(false);
      }
    };
    detectS3Mode();
  }, []);
  useEffect(() => {
    if (isS3Mode) setFolder(FolderService.getFolderIfSet());
  }, [isS3Mode]);
  const uploadAndSyncAvailable = !isS3Mode || folder === 'books';

  // Restore saved state on mount
  const savedState = settingsService.getInfoTableState();
  const initialPagination = savedState 
    ? savedState.pagination 
    : {
        pageIndex: settingsService.getInfoTablePageIndex(),
        pageSize: settingsService.getInfoTablePageSize(),
      };
  const initialFilters = savedState?.filters || {};
  const initialColumnOrder = savedState?.columnOrder;

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>(initialPagination);
  const [columnOrder, setColumnOrder] = React.useState<ColumnOrderState>(
    initialColumnOrder && Array.isArray(initialColumnOrder) && initialColumnOrder.length > 0 
      ? initialColumnOrder 
      : []
  );
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  // Editable page number in pagination (1-based display)
  const [pageInputValue, setPageInputValue] = React.useState(() => String((typeof initialPagination?.pageIndex === 'number' ? initialPagination.pageIndex : 0) + 1));
  // Track if pagination change is user-initiated (via button clicks)
  const isUserNavigationRef = useRef(false);

  // Filter state - restore from saved state
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>(initialFilters);
  const [openFilterDropdown, setOpenFilterDropdown] = useState<string | null>(null);
  const filterDropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const [queuedBooks, setQueuedBooks] = useState<Set<string>>(new Set());
  const [syncResults, setSyncResults] = useState<Map<string, { success: boolean; message: string }>>(new Map());
  const [syncStatusUpdates, setSyncStatusUpdates] = useState<Map<string, 'synced' | 'failed'>>(new Map());
  /** When Uploaded shows synced, treat approval.images as 'auto' for display if not already true/'true'/'auto' */
  const getEffectiveApprovalImages = useCallback((book: Book, isbn: string) => {
    if (syncStatusUpdates.get(isbn) === 'synced') {
      const v = book.approval?.images;
      if (v !== true && v !== 'true' && v !== 'auto') return 'auto';
    }
    return book.approval?.images;
  }, [syncStatusUpdates]);

  /** When Uploaded shows synced and book has enhanced text, treat approval.words as 'auto' for display if not already approved/auto */
  const getEffectiveApprovalWords = useCallback((book: Book, isbn: string) => {
    if (syncStatusUpdates.get(isbn) === 'synced') {
      const hasEnhancedText = book.text_extracted_enhanced === true || (book as { text_extracted_enhanced?: unknown }).text_extracted_enhanced === 'true';
      if (hasEnhancedText) {
        const v = book.approval?.words;
        if (v !== true && v !== 'true' && v !== 'auto') return 'auto';
      }
    }
    return book.approval?.words;
  }, [syncStatusUpdates]);

  /** Effective Uploaded status: override from syncStatusUpdates when present, else from book (for filter and dropdown). */
  const getEffectiveSyncStatus = useCallback((book: Book) => {
    if (book.in_all_books === false) return 'no_files';
    const override = syncStatusUpdates.get(book.isbn);
    if (override) return override;
    return book.production_file_sync_status || 'not_synced';
  }, [syncStatusUpdates]);

  // Refs for horizontal scrollbar synchronization
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const topScrollbarRef = useRef<HTMLDivElement | null>(null);
  const scrollbarSpacerRef = useRef<HTMLDivElement | null>(null);


  // State for spine data - only store the fields we need, keyed by ISBN
  interface SpineMetadata {
    audio_service: string;
    voice_name: string;
    tts_chars: number | null;
    tts_cost_azure: number | null;
    tts_cost_elevenlabs: number | null;
    changed_by: string[] | null;
    last_changed: string | null;
    fast_track: boolean | null;
    copyright: boolean | null;
  }
  const [spineMetadata, setSpineMetadata] = useState<Map<string, SpineMetadata>>(new Map());
  const [spineDataLoading, setSpineDataLoading] = useState(false);
  const spineLoadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasOpenedBookRef = useRef(false);
  const lastPaginationPageRef = useRef(0);

  // Get unique values for filterable columns
  const filterOptions = useMemo(() => {
    const options: Record<string, string[]> = {};
    
    // Publisher
    const publishers = [...new Set(books.map(book => book.publisher).filter(Boolean))].sort();
    options.publisher = publishers;
    
    // Series
    const series = [...new Set(books.map(book => book.series).filter(Boolean))].sort();
    options.series = series;
    
    // Text Extracted
    // Note: text_extracted can be 'basic'|'combined'|'blocked'|'enhanced' or boolean false
    // Normalize false to '' for consistent filtering
    const textExtractedSet = new Set<string>();
    books.forEach(book => {
      const value = book.text_extracted;
      if (value === null || value === undefined || value === '' || value === false) {
        textExtractedSet.add('');
      } else {
        textExtractedSet.add(typeof value === 'string' ? value : '');
      }
    });
    options.text_extracted = Array.from(textExtractedSet).sort();
    
    // Approval (combined) - predefined options; always include None and Other
    const approvalOptions = new Set<string>(['None', 'Other']);
    books.forEach(book => {
      const approval = book.approval;
      if (!approval) {
        approvalOptions.add('None');
      } else {
        if (approval.images === true || approval.images === 'true') {
          approvalOptions.add('Images Approved');
        } else if (approval.images === 'auto') {
          approvalOptions.add('Images Auto');
        } else if (approval.images === false || approval.images === 'false') {
          approvalOptions.add('Images Failed');
        }
        const effWords = getEffectiveApprovalWords(book, book.isbn);
        if (effWords === true || effWords === 'true') {
          approvalOptions.add('Words Approved');
        } else if (effWords === 'auto') {
          approvalOptions.add('Words Auto');
        } else if (effWords === false || effWords === 'false') {
          approvalOptions.add('Words Failed');
        }
        if (approval.voice === true || approval.voice === 'true') {
          approvalOptions.add('Voice Added');
        }
        // Other: approval exists but doesn't match any of the specific options above
        const effImages = getEffectiveApprovalImages(book, book.isbn);
        const matchesAny = (effImages === true || effImages === 'true' || effImages === 'auto' || effImages === false || effImages === 'false') ||
          (effWords === true || effWords === 'true' || effWords === 'auto' || effWords === false || effWords === 'false') ||
          (approval.voice === true || approval.voice === 'true');
        if (!matchesAny) approvalOptions.add('Other');
      }
    });
    options.approval = Array.from(approvalOptions).sort();
    
    // Approval Images - predefined options; always include None and Other
    const approvalImagesOptions = new Set<string>(['None', 'Other']);
    books.forEach(book => {
      const approval = book.approval;
      if (!approval || approval.images === null || approval.images === undefined || approval.images === '') {
        approvalImagesOptions.add('None');
      } else if (approval.images === true || approval.images === 'true') {
        approvalImagesOptions.add('Approved');
      } else if (approval.images === 'auto') {
        approvalImagesOptions.add('Auto');
      } else if (approval.images === false || approval.images === 'false') {
        approvalImagesOptions.add('Failed');
      } else {
        approvalImagesOptions.add('Other');
      }
    });
    approvalImagesOptions.add('Failed'); // Always show Fail option so user can filter to failed
    options.approval_images = Array.from(approvalImagesOptions).sort();
    
    // Approval Words - predefined options; use effective value when sync just completed
    const approvalWordsOptions = new Set<string>(['None', 'Other']);
    books.forEach(book => {
      const wordsValue = getEffectiveApprovalWords(book, book.isbn);
      if (wordsValue === null || wordsValue === undefined || wordsValue === '') {
        approvalWordsOptions.add('None');
      } else if (wordsValue === true || wordsValue === 'true') {
        approvalWordsOptions.add('Approved');
      } else if (wordsValue === 'auto') {
        approvalWordsOptions.add('Auto');
      } else if (wordsValue === false || wordsValue === 'false') {
        approvalWordsOptions.add('Failed');
      } else {
        approvalWordsOptions.add('Other');
      }
    });
    approvalWordsOptions.add('Failed'); // Always show Fail option so user can filter to failed
    options.approval_words = Array.from(approvalWordsOptions).sort();
    
    // Approval Voice - predefined options; always include None and Other
    const approvalVoiceOptions = new Set<string>(['None', 'Other']);
    books.forEach(book => {
      const approval = book.approval;
      if (!approval || approval.voice === null || approval.voice === undefined || approval.voice === '') {
        approvalVoiceOptions.add('None');
      } else if (approval.voice === true || approval.voice === 'true') {
        approvalVoiceOptions.add('Added');
      } else {
        approvalVoiceOptions.add('Other');
      }
    });
    options.approval_voice = Array.from(approvalVoiceOptions).sort();

    // In DB (lookup)
    const inDbOptions = new Set<string>();
    books.forEach(book => {
      inDbOptions.add(book.in_db === true ? 'true' : 'false');
    });
    options.in_db = Array.from(inDbOptions).sort();
    
    // Images Created
    const imagesCreatedOptions = new Set<string>();
    books.forEach(book => {
      if (book.images_created === true) {
        imagesCreatedOptions.add('true');
      } else {
        imagesCreatedOptions.add('false');
      }
    });
    options.images_created = Array.from(imagesCreatedOptions).sort();
    
    // Fast Track
    const fastTrackOptions = new Set<string>();
    books.forEach(book => {
      if (book.fast_track === true) {
        fastTrackOptions.add('true');
      } else if (book.fast_track === false) {
        fastTrackOptions.add('false');
      } else {
        fastTrackOptions.add('null');
      }
    });
    options.fast_track = Array.from(fastTrackOptions).sort();
    
    // Copyright
    const copyrightOptions = new Set<string>();
    books.forEach(book => {
      if (book.copyright === true) {
        copyrightOptions.add('true');
      } else if (book.copyright === false) {
        copyrightOptions.add('false');
      } else {
        copyrightOptions.add('null');
      }
    });
    options.copyright = Array.from(copyrightOptions).sort();
    
    // Production File Sync Status (include overrides so filter options match what we display)
    const syncStatusOptions = new Set<string>();
    books.forEach(book => {
      if (book.in_all_books === false) {
        syncStatusOptions.add('no_files');
        return;
      }
      const status = book.production_file_sync_status;
      if (status === 'synced') {
        syncStatusOptions.add('synced');
      } else if (status === 'failed') {
        syncStatusOptions.add('failed');
      } else if (status === 'syncing') {
        syncStatusOptions.add('syncing');
      } else {
        syncStatusOptions.add('not_synced');
      }
    });
    syncStatusUpdates.forEach((status) => syncStatusOptions.add(status));
    options.production_file_sync_status = Array.from(syncStatusOptions).sort();

    // DB Status (computed: In Sync, Out of Sync, Not ready)
    options.production_file_sync_db_status = ['In Sync', 'Out of Sync', 'Not ready'];

    // Format (from all-books); empty/null => "unknown"
    const formatOptions = [...new Set(books.map(book => (book.format && String(book.format).trim()) ? book.format : 'unknown'))].sort();
    options.format = formatOptions;

    // Voice service (audio_service) and voice name (from all-books list)
    const voiceServiceOptions = [...new Set(books.map(book => book.voice_service ?? book.audio_service ?? '-'))].sort();
    options.audio_service = voiceServiceOptions;
    const voiceNameOptions = [...new Set(books.map(book => book.voice_name ?? '-'))].sort();
    options.voice_name = voiceNameOptions;

    if (isFullAccessUser) {
      // File Type
      const fileTypes = [...new Set(books.map(book => book.file_type).filter(Boolean))].sort();
      options.file_type = fileTypes;
      // Audio Included
      const audioIncluded = [...new Set(books.map(book => book.audio_included).filter(Boolean))].sort();
      options.audio_included = audioIncluded;
      // Audio AI Ready
      const audioAiReady = [...new Set(books.map(book => book.audio_ai_ready).filter(Boolean))].sort();
      options.audio_ai_ready = audioAiReady;
      // Quizzes (quiz question count from DB)
      const quizQuestionCount = [...new Set(books.map(book => book.quiz_question_count).filter(v => v !== undefined && v !== ''))].sort();
      options.quiz_question_count = quizQuestionCount;
      // Extracted From (from all-books); empty/null/N/A => "unknown"
      const extractedFromOptions = [...new Set(books.map(book => {
        const v = book.extracted_from;
        if (!v || !String(v).trim() || v === 'N/A') return 'unknown';
        return v;
      }))].sort();
      options.extracted_from = extractedFromOptions;
      // Audio Inside (from all-books, boolean)
      const audioInsideOptions = new Set<string>();
      books.forEach(book => {
        if (book.audio_inside === true) {
          audioInsideOptions.add('true');
        } else if (book.audio_inside === false) {
          audioInsideOptions.add('false');
        } else {
          audioInsideOptions.add('null');
        }
      });
      options.audio_inside = Array.from(audioInsideOptions).sort();
      options.good_audio = ['true', 'false', '-'];
      options.include_list_consumer = ['true', 'false'];
      options.include_list_educator = ['true', 'false'];
    }

    return options;
  }, [books, syncStatusUpdates, getEffectiveApprovalImages, getEffectiveApprovalWords, isFullAccessUser]);

  // Filter books based on active filters
  const filteredBooks = useMemo(() => {
    return books.filter(book => {
      // Publisher filter
      if (activeFilters.publisher && activeFilters.publisher.length > 0) {
        if (!activeFilters.publisher.includes(book.publisher || '')) return false;
      }
      
      // Series filter
      if (activeFilters.series && activeFilters.series.length > 0) {
        if (!activeFilters.series.includes(book.series || '')) return false;
      }
      
      // Text Extracted filter
      if (activeFilters.text_extracted && activeFilters.text_extracted.length > 0) {
        const textValue = book.text_extracted || '';
        // Handle null/empty string matching
        const matches = activeFilters.text_extracted.some(filterValue => {
          if (filterValue === '' || filterValue === 'null') {
            return textValue === '' || textValue === null || textValue === undefined;
          }
          return textValue === filterValue;
        });
        if (!matches) return false;
      }
      
      // In DB filter
      if (activeFilters.in_db && activeFilters.in_db.length > 0) {
        const inDb = book.in_db === true ? 'true' : 'false';
        if (!activeFilters.in_db.includes(inDb)) return false;
      }
      
      // Approval Images filter (use effective value when Uploaded shows synced)
      if (activeFilters.approval_images && activeFilters.approval_images.length > 0) {
        const imagesValue = getEffectiveApprovalImages(book, book.isbn);
        const isNone = imagesValue === null || imagesValue === undefined || imagesValue === '';
        const isOther = !isNone && imagesValue !== true && imagesValue !== 'true' && imagesValue !== 'auto' && imagesValue !== false && imagesValue !== 'false';
        let matches = false;
        for (const filterValue of activeFilters.approval_images) {
          if (filterValue === 'None' && isNone) { matches = true; break; }
          if (filterValue === 'Other' && isOther) { matches = true; break; }
          if (filterValue === 'Approved' && (imagesValue === true || imagesValue === 'true')) { matches = true; break; }
          if (filterValue === 'Auto' && imagesValue === 'auto') { matches = true; break; }
          if (filterValue === 'Failed' && (imagesValue === false || imagesValue === 'false')) { matches = true; break; }
        }
        if (!matches) return false;
      }
      
      // Approval Words filter
      if (activeFilters.approval_words && activeFilters.approval_words.length > 0) {
        const wordsValue = getEffectiveApprovalWords(book, book.isbn);
        const isNone = wordsValue === null || wordsValue === undefined || wordsValue === '';
        const isOther = !isNone && wordsValue !== true && wordsValue !== 'true' && wordsValue !== false && wordsValue !== 'false' && wordsValue !== 'auto';
        let matches = false;
        for (const filterValue of activeFilters.approval_words) {
          if (filterValue === 'None' && isNone) { matches = true; break; }
          if (filterValue === 'Other' && isOther) { matches = true; break; }
          if (filterValue === 'Approved' && (wordsValue === true || wordsValue === 'true')) { matches = true; break; }
          if (filterValue === 'Auto' && wordsValue === 'auto') { matches = true; break; }
          if (filterValue === 'Failed' && (wordsValue === false || wordsValue === 'false')) { matches = true; break; }
        }
        if (!matches) return false;
      }
      
      // Approval Voice filter
      if (activeFilters.approval_voice && activeFilters.approval_voice.length > 0) {
        const approval = book.approval;
        const voiceValue = approval?.voice;
        const isNone = !approval || voiceValue === null || voiceValue === undefined || voiceValue === '';
        const isOther = !isNone && voiceValue !== true && voiceValue !== 'true';
        let matches = false;
        for (const filterValue of activeFilters.approval_voice) {
          if (filterValue === 'None' && isNone) { matches = true; break; }
          if (filterValue === 'Other' && isOther) { matches = true; break; }
          if (filterValue === 'Added' && (voiceValue === true || voiceValue === 'true')) { matches = true; break; }
        }
        if (!matches) return false;
      }
      
      // Images Created filter
      if (activeFilters.images_created && activeFilters.images_created.length > 0) {
        const imagesCreated = book.images_created === true ? 'true' : 'false';
        if (!activeFilters.images_created.includes(imagesCreated)) return false;
      }
      
      // Fast Track filter
      if (activeFilters.fast_track && activeFilters.fast_track.length > 0) {
        const fastTrack = book.fast_track === true ? 'true' : book.fast_track === false ? 'false' : 'null';
        if (!activeFilters.fast_track.includes(fastTrack)) return false;
      }
      
      // Copyright filter
      if (activeFilters.copyright && activeFilters.copyright.length > 0) {
        const copyright = book.copyright === true ? 'true' : book.copyright === false ? 'false' : 'null';
        if (!activeFilters.copyright.includes(copyright)) return false;
      }
      
      // Production File Sync Status filter (Files / Uploaded column; use effective status so just-synced rows match)
      if (activeFilters.production_file_sync_status && activeFilters.production_file_sync_status.length > 0) {
        const status = getEffectiveSyncStatus(book);
        if (!activeFilters.production_file_sync_status.includes(status)) return false;
      }

      // DB Status filter (Status column; use effective approval when Uploaded shows synced)
      if (activeFilters.production_file_sync_db_status && activeFilters.production_file_sync_db_status.length > 0) {
        const effectiveBook: Book = { ...book, approval: { ...book.approval, images: getEffectiveApprovalImages(book, book.isbn) } };
        if (!activeFilters.production_file_sync_db_status.includes(getDbSyncStatus(effectiveBook))) return false;
      }

      // Format filter (from all-books); empty => "unknown"
      if (activeFilters.format && activeFilters.format.length > 0) {
        const formatVal = (book.format && String(book.format).trim()) ? book.format : 'unknown';
        if (!activeFilters.format.includes(formatVal)) return false;
      }

      // Voice service filter (audio_service column)
      if (activeFilters.audio_service && activeFilters.audio_service.length > 0) {
        const voiceService = book.voice_service ?? book.audio_service ?? '-';
        if (!activeFilters.audio_service.includes(voiceService)) return false;
      }

      // Voice name filter
      if (activeFilters.voice_name && activeFilters.voice_name.length > 0) {
        const voiceName = book.voice_name ?? '-';
        if (!activeFilters.voice_name.includes(voiceName)) return false;
      }

      if (isFullAccessUser) {
        // File Type filter
        if (activeFilters.file_type && activeFilters.file_type.length > 0) {
          if (!activeFilters.file_type.includes(book.file_type || '')) return false;
        }
        if (activeFilters.audio_included && activeFilters.audio_included.length > 0) {
          if (!activeFilters.audio_included.includes(book.audio_included || '')) return false;
        }
        if (activeFilters.audio_ai_ready && activeFilters.audio_ai_ready.length > 0) {
          if (!activeFilters.audio_ai_ready.includes(book.audio_ai_ready || '')) return false;
        }
        if (activeFilters.quiz_question_count && activeFilters.quiz_question_count.length > 0) {
          if (!activeFilters.quiz_question_count.includes(book.quiz_question_count || '')) return false;
        }
        if (activeFilters.extracted_from && activeFilters.extracted_from.length > 0) {
          const ef = book.extracted_from;
          const extractedFromVal = (ef && String(ef).trim() && ef !== 'N/A') ? ef : 'unknown';
          if (!activeFilters.extracted_from.includes(extractedFromVal)) return false;
        }
        if (activeFilters.audio_inside && activeFilters.audio_inside.length > 0) {
          const audioInside = book.audio_inside === true ? 'true' : book.audio_inside === false ? 'false' : 'null';
          if (!activeFilters.audio_inside.includes(audioInside)) return false;
        }
        if (activeFilters.good_audio && activeFilters.good_audio.length > 0) {
          const goodAudio = book.good_audio === true ? 'true' : book.good_audio === false ? 'false' : '-';
          if (!activeFilters.good_audio.includes(goodAudio)) return false;
        }
        if (activeFilters.include_list_consumer && activeFilters.include_list_consumer.length > 0) {
          const consumer = book.include_list_consumer === true ? 'true' : book.include_list_consumer === false ? 'false' : '-';
          if (!activeFilters.include_list_consumer.includes(consumer)) return false;
        }
        if (activeFilters.include_list_educator && activeFilters.include_list_educator.length > 0) {
          const educator = book.include_list_educator === true ? 'true' : book.include_list_educator === false ? 'false' : '-';
          if (!activeFilters.include_list_educator.includes(educator)) return false;
        }
      }

      // Approval filter (AND logic - all selected filters must match) - legacy support
      if (activeFilters.approval && activeFilters.approval.length > 0) {
        const approval = book.approval;
        const effectiveImages = getEffectiveApprovalImages(book, book.isbn);
        const effectiveWords = getEffectiveApprovalWords(book, book.isbn);
        const approvalMatchesAnySpecific = approval && (
          (effectiveImages === true || effectiveImages === 'true' || effectiveImages === 'auto' || effectiveImages === false || effectiveImages === 'false') ||
          (effectiveWords === true || effectiveWords === 'true' || effectiveWords === 'auto' || effectiveWords === false || effectiveWords === 'false') ||
          (approval.voice === true || approval.voice === 'true')
        );
        for (const filterValue of activeFilters.approval) {
          let filterMatches = false;
          if (filterValue === 'None' && !approval) filterMatches = true;
          else if (filterValue === 'Other' && approval && !approvalMatchesAnySpecific) filterMatches = true;
          else if (approval) {
            if (filterValue === 'Images Approved' && (effectiveImages === true || effectiveImages === 'true')) filterMatches = true;
            else if (filterValue === 'Images Auto' && effectiveImages === 'auto') filterMatches = true;
            else if (filterValue === 'Images Failed' && (effectiveImages === false || effectiveImages === 'false')) filterMatches = true;
            else if (filterValue === 'Words Approved' && (effectiveWords === true || effectiveWords === 'true')) filterMatches = true;
            else if (filterValue === 'Words Auto' && effectiveWords === 'auto') filterMatches = true;
            else if (filterValue === 'Words Failed' && (effectiveWords === false || effectiveWords === 'false')) filterMatches = true;
            else if (filterValue === 'Voice Added' && (approval.voice === true || approval.voice === 'true')) filterMatches = true;
          }
          if (!filterMatches) return false;
        }
      }
      
      return true;
    });
  }, [books, activeFilters, syncStatusUpdates, getEffectiveApprovalImages, getEffectiveApprovalWords, getEffectiveSyncStatus, isFullAccessUser]);

  // Click outside handler for filter dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openFilterDropdown) {
        const dropdown = filterDropdownRefs.current[openFilterDropdown];
        if (dropdown && !dropdown.contains(event.target as Node)) {
          setOpenFilterDropdown(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openFilterDropdown]);

  // Track previous filters to detect actual changes
  const previousFiltersRef = useRef<string>(JSON.stringify(activeFilters));
  
  // Reset pagination when filters actually change (deep comparison)
  useEffect(() => {
    const currentFiltersStr = JSON.stringify(activeFilters);
    if (previousFiltersRef.current === currentFiltersStr) {
      // Filters haven't actually changed, skip reset
      return;
    }
    
    console.log('[BookTable] Filters changed, resetting pagination to 0');
    console.log('[BookTable] Stack trace:', new Error().stack);
    setPagination(prev => {
      console.log(`[BookTable] setPagination (filters) - from ${prev.pageIndex} to 0`);
      return { ...prev, pageIndex: 0 };
    });
    settingsService.setInfoTablePageIndex(0);
    currentPageRef.current = 0;
    previousFiltersRef.current = currentFiltersStr;
  }, [activeFilters]);

  // Update page size when settings change
  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      pageSize: settingsService.getInfoTablePageSize()
    }));
  }, []);

  // Initialize currentPageRef with restored pagination
  useEffect(() => {
    if (savedState) {
      console.log('[BookTable] Restored saved table state on mount:', savedState);
      currentPageRef.current = savedState.pagination.pageIndex;
      settingsService.setInfoTablePageIndex(savedState.pagination.pageIndex);
      settingsService.setInfoTablePageSize(savedState.pagination.pageSize);
    }
  }, []);

  // Track previous state to prevent unnecessary saves
  const previousStateRef = useRef<string>('');
  
  // Save table state whenever filters, pagination, column order, or search changes
  useEffect(() => {
    const currentState = {
      filters: activeFilters,
      pagination: {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      },
      columnOrder: columnOrder.length > 0 ? columnOrder : undefined,
      searchQuery: '', // Search is handled in BookList, not BookTable
    };
    const currentStateStr = JSON.stringify(currentState);
    
    // Only save if state actually changed
    if (previousStateRef.current !== currentStateStr) {
      settingsService.setInfoTableState(currentState);
      previousStateRef.current = currentStateStr;
    }
  }, [activeFilters, pagination.pageIndex, pagination.pageSize, columnOrder]);

  // Reset to page 1 when books actually change (different ISBNs or count), not just reference change
  const previousBooksRef = useRef<string>('');
  const booksInitializedRef = useRef(false);
  
  useEffect(() => {
    // Create a stable key from books (ISBNs and count) to detect actual changes
    const booksKey = books.map(b => b.isbn).sort().join(',') + `|${books.length}`;
    
    console.log(`[BookTable] Books effect - previous: "${previousBooksRef.current}", current: "${booksKey}", initialized: ${booksInitializedRef.current}`);
    
    // Only reset if the actual book list changed, not just the array reference
    // Skip on initial mount to avoid resetting when component first loads
    if (booksInitializedRef.current && previousBooksRef.current !== booksKey) {
      console.log('[BookTable] Books actually changed (different ISBNs or count), resetting pagination to 0');
      console.log('[BookTable] Stack trace:', new Error().stack);
      setPagination(prev => {
        console.log(`[BookTable] setPagination called - from ${prev.pageIndex} to 0`);
        return { ...prev, pageIndex: 0 };
      });
      settingsService.setInfoTablePageIndex(0);
      currentPageRef.current = 0;
    } else if (!booksInitializedRef.current) {
      console.log('[BookTable] Books initialized, not resetting pagination');
      booksInitializedRef.current = true;
    } else {
      console.log('[BookTable] Books array reference changed but content is the same, NOT resetting pagination');
    }
    
    previousBooksRef.current = booksKey;
  }, [books]);

  // Load spine data for visible books only (current page) after 2 seconds delay
  // Strategy: Load all spine data for visible books, then update table once
  const isLoadingRef = useRef(false);
  const loadedIsbnsRef = useRef<Set<string>>(new Set());
  const currentPageRef = useRef(pagination.pageIndex);
  
  useEffect(() => {
    // Clear any existing timeout and cancel any pending loads when pagination changes
    if (spineLoadTimeoutRef.current) {
      clearTimeout(spineLoadTimeoutRef.current);
      spineLoadTimeoutRef.current = null;
    }
    
    // Cancel any in-progress loads if pagination changed
    if (currentPageRef.current !== pagination.pageIndex) {
      console.log(`[BookTable] Pagination changed from ${currentPageRef.current} to ${pagination.pageIndex}, canceling any pending loads`);
      isLoadingRef.current = false;
      setSpineDataLoading(false);
      currentPageRef.current = pagination.pageIndex;
      lastPaginationPageRef.current = pagination.pageIndex;
    }

    // Reset flags when books change (not pagination)
    if (filteredBooks.length !== 0) {
      hasOpenedBookRef.current = false;
    }

    // Don't load if already loading
    if (isLoadingRef.current) {
      return;
    }

    const loadSpineData = async () => {
      // Don't load if a book has been opened or pagination changed
      if (hasOpenedBookRef.current) {
        isLoadingRef.current = false;
        return;
      }

      if (filteredBooks.length === 0) {
        isLoadingRef.current = false;
        return;
      }

      // Prevent concurrent loads
      if (isLoadingRef.current) {
        return;
      }
      isLoadingRef.current = true;

      setSpineDataLoading(true);
      
      try {
        const folder = FolderService.getFolder();
        if (!folder) {
          isLoadingRef.current = false;
          setSpineDataLoading(false);
          return;
        }

        // Check again before loading (in case book was opened or pagination changed during delay)
        if (hasOpenedBookRef.current || currentPageRef.current !== pagination.pageIndex) {
          console.log(`[BookTable] Canceling load - book opened or page changed (current: ${currentPageRef.current}, pagination: ${pagination.pageIndex})`);
          setSpineDataLoading(false);
          isLoadingRef.current = false;
          return;
        }

        // Get only the books visible on the current page
        const startIndex = pagination.pageIndex * pagination.pageSize;
        const endIndex = startIndex + pagination.pageSize;
        const visibleBooks = filteredBooks.slice(startIndex, endIndex);

        // Skip books we already have data for
        // Also skip DB-only rows (not in all-books) - never load spine for those
        const booksToLoad = visibleBooks
          .filter(book => book.in_all_books !== false)
          .filter(book => !loadedIsbnsRef.current.has(book.isbn));
        
        if (booksToLoad.length === 0) {
          // All books already loaded
          setSpineDataLoading(false);
          isLoadingRef.current = false;
          return;
        }

        console.log(`[BookTable] Loading spine data for ${booksToLoad.length} books`);

        // Load all spine data for visible books in parallel
        const spineMetadataPromises = booksToLoad.map(async (book) => {
          try {
            const spineUrl = FolderService.addFolderToUrl(`/api/books/${book.isbn}/spine`);
            const response = await authenticatedFetch(spineUrl);
            
            if (response.ok) {
              const spineData = await response.json();
              const bookData = spineData.book || {};
              
              // Check if status.json exists (spine.book.status field)
              let changed_by: string[] | null = null;
              let last_changed: string | null = null;
              
              if (bookData.status === 'status.json') {
                try {
                  const statusUrl = FolderService.addFolderToUrl(`/api/books/${book.isbn}/book-status`);
                  const statusResponse = await authenticatedFetch(statusUrl);
                  
                  if (statusResponse.ok) {
                    const statusData = await statusResponse.json();
                    changed_by = statusData.changed_by || null;
                    last_changed = statusData.last_changed || null;
                    console.log(`[BookTable] Loaded status for ${book.isbn}:`, { changed_by, last_changed });
                  }
                } catch (statusError) {
                  // Status file doesn't exist or failed to load - this is OK
                  console.log(`[BookTable] Status not available for ${book.isbn}`);
                }
              }
              
              // Check if copyright section exists and has at least one page
              const hasCopyright = spineData.copyright && Array.isArray(spineData.copyright) && spineData.copyright.length > 0;
              
              const metadata: SpineMetadata = {
                audio_service: bookData.voice_service || '-',
                voice_name: bookData.voice_name || '-',
                tts_chars: bookData.tts_chars ?? null,
                tts_cost_azure: bookData.tts_cost_azure ?? null,
                tts_cost_elevenlabs: bookData.tts_cost_elevenlabs ?? null,
                changed_by,
                last_changed,
                fast_track: bookData.fast_track ?? null,
                copyright: hasCopyright ? true : false,
              };
              
              console.log(`[BookTable] Returning metadata for ISBN ${book.isbn}:`, { changed_by, last_changed });
              
              return {
                isbn: book.isbn,
                metadata
              };
            }
          } catch (error) {
            console.error(`[BookTable] Error loading spine data for ${book.isbn}:`, error);
          }
          
          // Return default values if spine load failed
          return {
            isbn: book.isbn,
            metadata: {
              audio_service: '-',
              voice_name: '-',
              tts_chars: null,
              tts_cost_azure: null,
              tts_cost_elevenlabs: null,
              changed_by: null,
              last_changed: null,
              fast_track: null,
              copyright: false,
            } as SpineMetadata
          };
        });

        // Check one more time before loading (in case pagination changed)
        if (hasOpenedBookRef.current || currentPageRef.current !== pagination.pageIndex) {
          console.log(`[BookTable] Canceling load before requests - page changed`);
          setSpineDataLoading(false);
          isLoadingRef.current = false;
          return;
        }

        // Wait for all spine data to load
        console.log(`[BookTable] Waiting for ${spineMetadataPromises.length} spine requests for page ${pagination.pageIndex}...`);
        const results = await Promise.all(spineMetadataPromises);
        console.log(`[BookTable] All spine data loaded for page ${pagination.pageIndex}`);
        
        // Final check before updating - if page changed during loading, discard results
        if (hasOpenedBookRef.current || currentPageRef.current !== pagination.pageIndex) {
          console.log(`[BookTable] Discarding loaded data - page changed during load (loaded for: ${currentPageRef.current}, current: ${pagination.pageIndex})`);
          setSpineDataLoading(false);
          isLoadingRef.current = false;
          return;
        }
        
        // Update the Map with all metadata at once (single state update)
        // Wrap in try-catch to prevent crashes from invalid data
        try {
          setSpineMetadata(prev => {
            const newMetadata = new Map(prev);
            // Filter out any undefined/null results and ensure ISBN matches
            const validResults = results.filter((result): result is { isbn: string; metadata: SpineMetadata } => {
              return result !== null && result !== undefined && !!result.isbn && result.metadata !== null && result.metadata !== undefined;
            });
            
            validResults.forEach(({ isbn, metadata }) => {
              console.log(`[BookTable] Setting metadata for ISBN ${isbn}:`, { 
                changed_by: metadata.changed_by, 
                last_changed: metadata.last_changed,
                audio_service: metadata.audio_service 
              });
              newMetadata.set(isbn, metadata);
              loadedIsbnsRef.current.add(isbn); // Track loaded ISBNs in ref
            });
            console.log(`[BookTable] Updated spine metadata, total entries: ${newMetadata.size}, valid results: ${validResults.length}`);
            return newMetadata;
          });
        } catch (error) {
          console.error('[BookTable] Error updating spine metadata:', error);
          // Don't crash - just log the error
        }
      } catch (error) {
        console.error('[BookTable] Error loading spine data:', error);
      } finally {
        setSpineDataLoading(false);
        isLoadingRef.current = false;
      }
    };

    // Wait 2 seconds before loading
    spineLoadTimeoutRef.current = setTimeout(() => {
      loadSpineData();
    }, 2000);

    // Cleanup timeout on unmount or when books/filters/pagination change
    return () => {
      if (spineLoadTimeoutRef.current) {
        clearTimeout(spineLoadTimeoutRef.current);
        spineLoadTimeoutRef.current = null;
      }
    };
  }, [filteredBooks.length, pagination.pageIndex, pagination.pageSize]);

  // Track when a book is opened
  const handleOpenBook = (isbn: string) => {
    hasOpenedBookRef.current = true;
    // Clear any pending spine load
    if (spineLoadTimeoutRef.current) {
      clearTimeout(spineLoadTimeoutRef.current);
      spineLoadTimeoutRef.current = null;
    }
    onOpenBook(isbn);
  };

  // Track pagination changes and cancel any pending loads immediately
  useEffect(() => {
    if (currentPageRef.current !== pagination.pageIndex) {
      console.log(`[BookTable] Pagination changed to ${pagination.pageIndex}, canceling all loads`);
      currentPageRef.current = pagination.pageIndex;
      lastPaginationPageRef.current = pagination.pageIndex;
      isLoadingRef.current = false;
      setSpineDataLoading(false);
      
      // Clear any pending spine load
      if (spineLoadTimeoutRef.current) {
        clearTimeout(spineLoadTimeoutRef.current);
        spineLoadTimeoutRef.current = null;
      }
    }
  }, [pagination.pageIndex]);

  // Filter functions
  const toggleFilter = (columnId: string, value: string) => {
    setActiveFilters(prev => {
      const currentFilters = prev[columnId] || [];
      const newFilters = currentFilters.includes(value)
        ? currentFilters.filter(v => v !== value)
        : [...currentFilters, value];
      
      return {
        ...prev,
        [columnId]: newFilters.length > 0 ? newFilters : undefined
      };
    });
  };

  const clearFilter = (columnId: string) => {
    setActiveFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[columnId];
      return newFilters;
    });
  };

  const isFilterActive = (columnId: string) => {
    return activeFilters[columnId] && activeFilters[columnId].length > 0;
  };

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return Object.keys(activeFilters).some(key => activeFilters[key] && activeFilters[key].length > 0);
  }, [activeFilters]);

  // Notify parent when filter state changes
  useEffect(() => {
    if (onFiltersChange) {
      onFiltersChange(hasActiveFilters);
    }
  }, [hasActiveFilters, onFiltersChange]);

  // Notify parent when pagination changes
  useEffect(() => {
    if (onPaginationChange) {
      onPaginationChange(pagination.pageIndex);
    }
  }, [pagination.pageIndex, onPaginationChange]);

  // Keep page input in sync with actual page (e.g. after clicking Prev/Next)
  useEffect(() => {
    setPageInputValue(String(pagination.pageIndex + 1));
  }, [pagination.pageIndex]);

  // Clear all filters function
  const clearAllFilters = useCallback(() => {
    setActiveFilters({});
    // Reset pagination to page 1
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
    settingsService.setInfoTablePageIndex(0);
    currentPageRef.current = 0;
    // Reset column order to default (empty array means use default)
    setColumnOrder([]);
  }, []);

  // Expose clearAllFilters via ref if provided
  useEffect(() => {
    if (clearFiltersRef) {
      clearFiltersRef.current = clearAllFilters;
    }
  }, [clearFiltersRef, clearAllFilters]);

  // Reset selection when filters change
  useEffect(() => {
    setRowSelection({});
  }, [activeFilters]);

  const [showCopyIsbnsModal, setShowCopyIsbnsModal] = useState(false);
  const [dbSyncModal, setDbSyncModal] = useState<{
    payload: { updates: Array<{ id: string; fields: Record<string, unknown> }> };
    ineligibleList?: Array<{ isbn: string; title?: string }>;
  } | null>(null);

  // Single shared poller: one interval, one getSyncStatus() per period regardless of queue size
  const syncPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const queuedBooksRef = useRef<Set<string>>(new Set());
  const queueStartTimeRef = useRef<Map<string, number>>(new Map());
  const currentFolderRef = useRef<string | null>(null);
  const booksRef = useRef<Book[]>([]);
  const onSyncCompletedRef = useRef<BookTableProps['onSyncCompleted']>(null);
  currentFolderRef.current = currentFolder ?? null;
  queuedBooksRef.current = queuedBooks;
  booksRef.current = books;
  onSyncCompletedRef.current = onSyncCompleted;

  const ensureSyncPollerRunning = useCallback(() => {
    if (syncPollIntervalRef.current) return;
    const POLL_MS = 5000;
    const TIMEOUT_MS = 300000; // 5 min per book
    syncPollIntervalRef.current = setInterval(async () => {
      try {
        const folder = currentFolderRef.current;
        if (!folder) return;
        const { productionFileSyncService } = await import('../services/ProductionFileSyncService');
        const status = await productionFileSyncService.getSyncStatus();
        const queued = new Set(queuedBooksRef.current);
        const toRemove: Array<{ isbn: string; success: boolean; message: string; statusUpdate: 'synced' | 'failed' }> = [];
        const now = Date.now();
        for (const isbn of queued) {
          const startTime = queueStartTimeRef.current.get(isbn) ?? now;
          if (now - startTime > TIMEOUT_MS) {
            toRemove.push({ isbn, success: false, message: 'Timed out', statusUpdate: 'failed' });
            queueStartTimeRef.current.delete(isbn);
            continue;
          }
          const inPending = status.pending.some((item: { isbn: string; folder: string }) => item.isbn === isbn && item.folder === folder);
          const inActive = status.active.find((item: { isbn: string; folder: string; status?: string; error?: string }) => item.isbn === isbn && item.folder === folder);
          if (inActive && inActive.status === 'failed') {
            toRemove.push({ isbn, success: false, message: inActive.error || 'Sync failed', statusUpdate: 'failed' });
            queueStartTimeRef.current.delete(isbn);
            continue;
          }
          if (inPending || inActive) continue; // still in progress
          const inCompleted = status.completed?.find((item: { isbn: string; folder: string }) => item.isbn === isbn && item.folder === folder);
          const inFailed = status.failed?.find((item: { isbn: string; folder: string; error?: string }) => item.isbn === isbn && item.folder === folder);
          if (inCompleted) {
            toRemove.push({ isbn, success: true, message: 'Sync completed successfully', statusUpdate: 'synced' });
            queueStartTimeRef.current.delete(isbn);
          } else if (inFailed) {
            toRemove.push({ isbn, success: false, message: inFailed.error || 'Sync failed', statusUpdate: 'failed' });
            queueStartTimeRef.current.delete(isbn);
          } else {
            // No longer in queue/active and not in completed/failed (e.g. server trimmed) — treat as success
            toRemove.push({ isbn, success: true, message: 'Sync completed successfully', statusUpdate: 'synced' });
            queueStartTimeRef.current.delete(isbn);
          }
        }
        if (toRemove.length === 0) return;
        setQueuedBooks(prev => {
          const next = new Set(prev);
          toRemove.forEach(({ isbn }) => next.delete(isbn));
          if (next.size === 0 && syncPollIntervalRef.current) {
            clearInterval(syncPollIntervalRef.current);
            syncPollIntervalRef.current = null;
          }
          return next;
        });
        setSyncResults(prev => {
          const next = new Map(prev);
          toRemove.forEach(({ isbn, success, message }) => next.set(isbn, { success, message }));
          return next;
        });
        setSyncStatusUpdates(prev => {
          const next = new Map(prev);
          toRemove.forEach(({ isbn, statusUpdate }) => next.set(isbn, statusUpdate));
          return next;
        });
        // Update in-memory approval for synced books (match ProductionFileSyncService logic)
        const syncedIsbns = toRemove.filter(({ success, statusUpdate }) => success && statusUpdate === 'synced').map(({ isbn }) => isbn);
        if (syncedIsbns.length > 0 && onSyncCompletedRef.current) {
          const bookList = booksRef.current;
          const updates: Array<{ isbn: string; approval: { images?: string; words?: string } }> = [];
          for (const isbn of syncedIsbns) {
            const book = bookList.find((b) => b.isbn === isbn);
            if (!book) continue;
            const approvalUpdate: { images?: string; words?: string } = {};
            const currentImages = book.approval?.images;
            if (currentImages !== true && currentImages !== 'true' && currentImages !== 'auto') {
              approvalUpdate.images = 'auto';
            }
            const hasEnhancedText = book.text_extracted_enhanced === true;
            if (hasEnhancedText) {
              const currentWords = book.approval?.words;
              if (currentWords !== true && currentWords !== 'true' && currentWords !== 'auto') {
                approvalUpdate.words = 'auto';
              }
            }
            if (Object.keys(approvalUpdate).length > 0) {
              updates.push({ isbn, approval: approvalUpdate });
            }
          }
          if (updates.length > 0) {
            onSyncCompletedRef.current(updates);
          }
        }
      } catch (error) {
        console.error('[BookTable] Error polling sync status:', error);
      }
    }, POLL_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (syncPollIntervalRef.current) {
        clearInterval(syncPollIntervalRef.current);
        syncPollIntervalRef.current = null;
      }
    };
  }, []);

  const BATCH_SIZE = 100;

  /**
   * Single code path for production-file-sync: queue ISBNs via batch API.
   * Used by row button (syncIsbns([isbn])), sync selected, and bulk modal.
   * Caller may pass onProgress for bulk UI (sent/received/done).
   */
  const syncIsbns = useCallback(
    async (
      isbns: string[],
      options?: { onProgress?: (sent: number, received: number, total: number, done: boolean) => void }
    ) => {
      const toSync = isbns.filter((isbn) => !queuedBooks.has(isbn) && !syncResults.has(isbn));
      if (toSync.length === 0) return;
      if (!currentFolder) {
        throw new Error('Folder is not set. Please select a folder first.');
      }
      const folder = currentFolder;
      window.dispatchEvent(new CustomEvent('syncTriggered'));
      ensureSyncPollerRunning();
      const total = toSync.length;
      let sent = 0;
      let received = 0;
      const { productionFileSyncService } = await import('../services/ProductionFileSyncService');
      for (let i = 0; i < toSync.length; i += BATCH_SIZE) {
        const chunk = toSync.slice(i, i + BATCH_SIZE);
        const result = await productionFileSyncService.addToQueueBatch(chunk, folder);
        const accepted = result.success ? result.accepted : [];
        const rejected = result.success ? result.rejected : chunk.map((isbn) => ({ isbn, reason: result.error || 'Request failed' }));
        const now = Date.now();
        for (const isbn of accepted) {
          queueStartTimeRef.current.set(isbn, now);
        }
        setQueuedBooks((prev) => {
          const next = new Set(prev);
          accepted.forEach((isbn) => next.add(isbn));
          return next;
        });
        setSyncResults((prev) => {
          const next = new Map(prev);
          accepted.forEach((isbn) => next.delete(isbn));
          rejected.forEach(({ isbn, reason }) => next.set(isbn, { success: false, message: reason }));
          return next;
        });
        setSyncStatusUpdates((prev) => {
          const next = new Map(prev);
          accepted.forEach((isbn) => next.delete(isbn));
          rejected.forEach(({ isbn }) => next.set(isbn, 'failed'));
          return next;
        });
        sent += chunk.length;
        received += accepted.length;
        options?.onProgress?.(sent, received, total, sent >= total);
      }
    },
    [currentFolder, queuedBooks, syncResults, ensureSyncPollerRunning]
  );

  const handleSyncBookRef = useRef<(isbn: string) => void>(null as any);
  const handleSyncBook = useCallback(
    (isbn: string) => {
      if (queuedBooks.has(isbn) || syncResults.has(isbn)) return;
      syncIsbns([isbn]);
    },
    [queuedBooks, syncResults, syncIsbns]
  );
  handleSyncBookRef.current = handleSyncBook;

  const DB_SYNC_BATCH_SIZE = 100;

  const handleConfirmDbSync = useCallback(async () => {
    if (!dbSyncModal) return;
    const { payload } = dbSyncModal;
    const wasBulk = payload.updates.length > 1;
    setDbSyncModal(null);
    try {
      const { FolderService } = await import('../services/FolderService');
      const { authenticatedFetch } = await import('../utils/api');
      const { toast } = await import('sonner');
      const url = FolderService.addFolderToUrl('/api/books/sync-to-admin-tool');
      const updates = payload.updates;
      let lastQa: { ok: boolean; error?: string } | undefined;
      let lastProd: { ok: boolean; error?: string } | undefined;
      for (let i = 0; i < updates.length; i += DB_SYNC_BATCH_SIZE) {
        const chunk = updates.slice(i, i + DB_SYNC_BATCH_SIZE);
        const res = await authenticatedFetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: chunk }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          alert(data?.error || data?.details || `Request failed: ${res.status}`);
          return;
        }
        const qa = data.qa as { ok: boolean; response?: string; error?: string } | undefined;
        const prod = data.prod as { ok: boolean; response?: string; error?: string } | undefined;
        lastQa = qa;
        lastProd = prod;
        if (!data.success || !qa?.ok || !prod?.ok) {
          const parts: string[] = [];
          if (qa) parts.push(`QA: ${qa.ok ? 'OK' : (qa.error || 'failed')}`);
          if (prod) parts.push(`PROD: ${prod.ok ? 'OK' : (prod.error || 'failed')}`);
          toast.warning(parts.length ? parts.join(' · ') : 'Sync completed with errors');
          if (qa?.error || prod?.error) {
            console.log('Sync to admin tool responses:', { qa: qa?.response || qa?.error, prod: prod?.response || prod?.error });
          }
          return;
        }
      }
      if (lastQa?.ok && lastProd?.ok) {
        if (wasBulk) setRowSelection({});
        toast.success('Synced to QA and PROD');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Sync failed');
    }
  }, [dbSyncModal]);

  // Listen for sync selected books event (uses same syncIsbns path as bulk)
  useEffect(() => {
    const handleSyncSelected = async () => {
      const selectedIsbns = Object.keys(rowSelection);
      if (selectedIsbns.length === 0) return;
      await syncIsbns(selectedIsbns);
      setRowSelection({});
    };

    window.addEventListener('syncSelectedBooks', handleSyncSelected);
    return () => {
      window.removeEventListener('syncSelectedBooks', handleSyncSelected);
    };
  }, [rowSelection, syncIsbns]);

  // Listen for bulk upload (paste ISBNs from modal); uses same syncIsbns path with progress
  useEffect(() => {
    const handleBulkUpload = async (event: Event) => {
      const customEvent = event as CustomEvent<{ isbns: string[] }>;
      const isbns = customEvent.detail?.isbns;
      if (!isbns || !Array.isArray(isbns) || isbns.length === 0) return;
      await syncIsbns(isbns, {
        onProgress: (sent, received, total, done) => {
          window.dispatchEvent(new CustomEvent('bulkUploadProgress', { detail: { sent, received, total, done } }));
        },
      });
    };
    window.addEventListener('bulkUploadIsbns', handleBulkUpload);
    return () => window.removeEventListener('bulkUploadIsbns', handleBulkUpload);
  }, [syncIsbns]);

  // Listen for filter preset events
  useEffect(() => {
    const handleApplyFilterPreset = (event: Event) => {
      const customEvent = event as CustomEvent<{ filters: Record<string, string[]> }>;
      const newFilters = customEvent.detail.filters;
      
      // Clear all current filters except list (which is handled by BookList)
      setActiveFilters(newFilters);
      
      // Reset to first page
      setPagination(prev => ({ ...prev, pageIndex: 0 }));
    };

    window.addEventListener('applyFilterPreset', handleApplyFilterPreset as EventListener);
    return () => {
      window.removeEventListener('applyFilterPreset', handleApplyFilterPreset as EventListener);
    };
  }, []);

  const columns = useMemo(
    () => [
      ...(isFullAccessUser ? [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => (
          <input
            type="checkbox"
            checked={table.getIsAllPageRowsSelected()}
            onChange={(e) => {
              if (e.target.checked) {
                table.toggleAllPageRowsSelected(true);
              } else {
                table.toggleAllPageRowsSelected(false);
              }
            }}
            style={{ cursor: 'pointer' }}
            title="Select all visible rows"
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={row.getIsSelected()}
            onChange={(e) => {
              row.toggleSelected(e.target.checked);
            }}
            disabled={!row.getCanSelect()}
            style={{ cursor: row.getCanSelect() ? 'pointer' : 'not-allowed', opacity: row.getCanSelect() ? 1 : 0.5 }}
            title="Select row"
          />
        ),
        enableSorting: false,
        size: 50,
      }),
      ] : []),
      columnHelper.accessor('isbn', {
        header: () => (
          <span
            onClick={(e) => {
              e.stopPropagation();
              setShowCopyIsbnsModal(true);
            }}
            style={{ cursor: 'pointer' }}
            title="Click to copy all ISBNs in current list"
          >
            ISBN
          </span>
        ),
        meta: { tooltip: 'International Standard Book Number - Click to copy all ISBNs' },
        cell: (info) => {
          const row = info.row.original;
          const isbn = info.getValue();
          if (row.in_all_books === false) {
            return <span className="isbn-text">{isbn}</span>;
          }
          return (
            <a
              href="#"
              className="isbn-link"
              onClick={(e) => {
                e.preventDefault();
                handleOpenBook(isbn);
              }}
            >
              {isbn}
            </a>
          );
        },
      }),
      ...(isFullAccessUser ? [
      columnHelper.display({
        id: 'prod',
        header: () => <img src="/favicon.ico" alt="Prod" style={{ width: '16px', height: '16px' }} />,
        meta: { tooltip: 'View book on production website' },
        cell: (info) => {
          const isbn = info.row.original.isbn;
          return (
            <div style={{ textAlign: 'center' }}>
              <a 
                href={`https://www.getcurioso.com/book-details/${isbn}`}
                target="_blank"
                rel="noopener noreferrer"
                className="prod-link"
                style={{ textTransform: 'none' }}
              >
                www
              </a>
            </div>
          );
        },
      }),
      ] : []),
      columnHelper.accessor('in_db', {
        id: 'in_db',
        header: 'In DB',
        enableSorting: false,
        meta: { tooltip: 'Present in db-book-data/book-data.csv (lookup)' },
        cell: (info) => {
          const value = info.getValue();
          if (value === true) {
            return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-yes" title="In DB">✓</span></div>;
          }
          return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-unknown" title="Not in DB" style={{ lineHeight: '1.25rem' }}>&#8203;</span></div>;
        },
      }),
      columnHelper.accessor('images_created', {
        id: 'images_created',
        header: '🖼️',
        enableSorting: false,
        meta: { tooltip: 'Image conversion status' },
        cell: (info) => {
          if (info.row.original.in_all_books === false) {
            return (
              <div style={{ textAlign: 'center' }}>
                <span className="fast-track-badge fast-track-yes" title="Not converted" style={{ backgroundColor: '#ef4444' }}>✗</span>
              </div>
            );
          }
          const value = info.getValue();
          if (value === true) {
            return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-yes" title="Images Created">✓</span></div>;
          }
          return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-unknown" title="not set" style={{ lineHeight: '1.25rem' }}>&#8203;</span></div>;
        },
      }),
      columnHelper.accessor('text_extracted', {
        id: 'text_extracted',
        header: '📝',
        meta: { tooltip: 'Text extraction status' },
        cell: (info) => {
          const value = info.getValue();
          const isEnhanced = value === 'enhanced';
          const badgeClass = isEnhanced ? 'text-extracted-badge-enhanced' : 'text-extracted-badge-default';
          return (
            <div style={{ textAlign: 'center' }}>
              <span className={badgeClass}>
                {value || 'N/A'}
              </span>
            </div>
          );
        },
      }),
      columnHelper.accessor('approval', {
        id: 'approval_images',
        header: '🖼️',
        meta: { tooltip: 'Images approval status' },
        cell: (info) => {
          const book = info.row.original;
          const value = getEffectiveApprovalImages(book, book.isbn);
          if (value === true || value === 'true') {
            return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-yes" title="Images Approved">✓</span></div>;
          }
          if (value === 'auto') {
            return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-auto" title="Images Auto (uploaded without manual approval)">✓</span></div>;
          }
          if (value === false || value === 'false') {
            return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-yes" title="Images Failed" style={{ backgroundColor: '#ef4444' }}>✗</span></div>;
          }
          return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-unknown" title="not set" style={{ lineHeight: '1.25rem' }}>&#8203;</span></div>;
        },
      }),
      columnHelper.accessor('approval', {
        id: 'approval_words',
        header: '📝',
        meta: { tooltip: 'Words approval status' },
        cell: (info) => {
          const book = info.row.original;
          const value = getEffectiveApprovalWords(book, book.isbn);
          if (value === true || value === 'true') {
            return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-yes" title="Words Approved">✓</span></div>;
          }
          if (value === 'auto') {
            return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-auto" title="Words Auto (uploaded without manual approval)">✓</span></div>;
          }
          if (value === false || value === 'false') {
            return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-yes" title="Words Failed" style={{ backgroundColor: '#ef4444' }}>✗</span></div>;
          }
          return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-unknown" title="not set" style={{ lineHeight: '1.25rem' }}>&#8203;</span></div>;
        },
      }),
      columnHelper.accessor('approval', {
        id: 'approval_voice',
        header: '🎤',
        meta: { tooltip: 'Voice approval status' },
        cell: (info) => {
          const book = info.row.original;
          const approval = book.approval;
          const value = approval?.voice;
          const voiceService = book.voice_service ?? book.audio_service;

          if (value === true || value === 'true') {
            if (voiceService === 'curioso-custom') {
              return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-yes" title="Voice Added (Curioso Custom)">🧑</span></div>;
            }
            return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-yes" title="Voice Added">✓</span></div>;
          }
          return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-unknown" title="not set" style={{ lineHeight: '1.25rem' }}>&#8203;</span></div>;
        },
      }),
      columnHelper.accessor('fast_track', {
        header: 'FT',
        meta: { tooltip: 'Fast track status' },
        cell: (info) => {
          const value = info.getValue();
          if (value === true) {
            return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-yes" title="Fast Track">✓</span></div>;
          } else if (value === false) {
            return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-no" title="Not Fast Track">-</span></div>;
          }
          return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-unknown" title="not set" style={{ lineHeight: '1.25rem' }}>&#8203;</span></div>;
        },
      }),
      columnHelper.accessor('copyright', {
        header: '©',
        meta: { tooltip: 'Copyright section presence' },
        cell: (info) => {
          const value = info.getValue();
          if (value === true) {
            return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-yes" title="Has Copyright Section">✓</span></div>;
          } else if (value === false) {
            return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-no" title="No Copyright Section">-</span></div>;
          }
          return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-unknown" title="not set" style={{ lineHeight: '1.25rem' }}>&#8203;</span></div>;
        },
      }),
      // Upload columns - only for full-access users. Order: Files group (Uploaded, Upload), then DB group (Status, Sync).
      ...(isFullAccessUser ? [
        columnHelper.accessor('production_file_sync_status', {
          id: 'production_file_sync_status',
          header: 'Uploaded',
          enableSorting: false,
          meta: { tooltip: 'Have the latest files been uploaded' },
          cell: (info) => {
            const isbn = info.row.original.isbn;
            const statusUpdate = syncStatusUpdates.get(isbn);
            const value = statusUpdate || info.getValue();
            if (value === 'synced') {
              return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-yes" title="Synced">✓</span></div>;
            } else if (value === 'failed') {
              return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-unknown" title="Failed" style={{ backgroundColor: '#ef4444', lineHeight: '1.25rem' }}>&#8203;</span></div>;
            } else {
              return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-unknown" title="Not synced" style={{ lineHeight: '1.25rem' }}>&#8203;</span></div>;
            }
          },
        }),
        ...(uploadAndSyncAvailable ? [
          columnHelper.display({
            id: 'production_file_sync',
            header: 'Upload',
            meta: { tooltip: 'Upload files to production' },
            cell: (info) => {
              const isbn = info.row.original.isbn;
              if (info.row.original.in_all_books === false) {
                return <div style={{ textAlign: 'center' }}><span style={{ lineHeight: '1.25rem' }}>&#8203;</span></div>;
              }
              const isQueued = queuedBooks.has(isbn);
              const result = syncResults.get(isbn);
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                  <button
                    onClick={() => handleSyncBook(isbn)}
                    disabled={isQueued}
                    style={{
                      padding: '4px 8px',
                      fontSize: '11px',
                      borderRadius: '4px',
                      cursor: isQueued ? 'not-allowed' : 'pointer',
                      border: '1px solid var(--border-primary)',
                      backgroundColor: isQueued ? 'var(--bg-secondary)' : 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      minWidth: '60px'
                    }}
                    title={isQueued ? 'Queued for upload...' : result ? (result.success ? 'Uploaded' : 'Upload failed') : 'Upload files to production'}
                  >
                    {isQueued ? '⏳' : result && result.success ? 'Uploaded' : result && !result.success ? '✗' : 'Upload'}
                  </button>
                </div>
              );
            },
          }),
        ] : []),
        columnHelper.display({
          id: 'production_file_sync_db_status',
          header: 'Status',
          meta: { tooltip: 'In Sync: file type and audio flag match DB. Out of Sync: mismatch. Not ready: approval not done yet.' },
          cell: (info) => {
            const book = info.row.original;
            const effectiveBook: Book = { ...book, approval: { ...book.approval, images: getEffectiveApprovalImages(book, book.isbn) } };
            const status = getDbSyncStatus(effectiveBook);
            if (status === 'In Sync') {
              return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-yes" title="In Sync">✓</span></div>;
            }
            if (status === 'Out of Sync') {
              return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-yes" title="Out of Sync" style={{ backgroundColor: '#ef4444' }}>✗</span></div>;
            }
            return <div style={{ textAlign: 'center' }}><span className="fast-track-badge fast-track-unknown" title="Not ready" style={{ lineHeight: '1.25rem' }}>&#8203;</span></div>;
          },
        }),
        columnHelper.display({
          id: 'production_file_sync_db',
          header: 'Sync',
          meta: { tooltip: 'Sync DB fields (File_Type, Audio_Ai_Ready) to admin tool. Shown when in all-books, images created, and images approved.' },
          cell: (info) => {
            const book = info.row.original;
            const inAllBooks = book.in_all_books !== false;
            const imagesCreated = book.images_created === true;
            const effectiveImages = getEffectiveApprovalImages(book, book.isbn);
            const imagesApproved = effectiveImages === true || effectiveImages === 'true' || effectiveImages === 'auto';
            const effectiveWords = getEffectiveApprovalWords(book, book.isbn);
            const wordsApproved = effectiveWords === true || effectiveWords === 'true' || effectiveWords === 'auto';
            const showSync = inAllBooks && imagesCreated && (!requireImageApproval || imagesApproved);

            if (!showSync) {
              return <div style={{ textAlign: 'center' }}><span style={{ lineHeight: '1.25rem' }}>&#8203;</span></div>;
            }

            const payload = {
              updates: [{
                id: book.isbn,
                fields: {
                  File_Type: 'IMG',
                  Audio_Ai_Ready: wordsApproved ? 1 : 0,
                },
              }],
            };

            return (
              <div style={{ textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={() => setDbSyncModal({ payload })}
                  style={{
                    padding: '4px 8px',
                    fontSize: '11px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    border: '1px solid var(--border-primary)',
                    backgroundColor: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                  }}
                  title="Sync File_Type and Audio_Ai_Ready to admin tool DB"
                >
                  Sync
                </button>
              </div>
            );
          },
        }),
      ] : []),
      columnHelper.accessor('book_title', {
        header: 'Title',
        meta: { tooltip: 'Book title from database' },
        cell: (info) => (
          <a 
            href="#" 
            className="book-title-link" 
            onClick={(e) => { 
              e.preventDefault(); 
              handleOpenBook(info.row.original.isbn); 
            }}
            title={info.getValue()}
          >
            {info.getValue() || 'No Title'}
          </a>
        ),
      }),
      columnHelper.accessor('publisher', {
        header: 'Publisher',
        meta: { tooltip: 'Publisher from database' },
        cell: (info) => (
          <span className="publisher-text" title={info.getValue()}>
            {info.getValue() || '-'}
          </span>
        ),
      }),
      columnHelper.accessor('series', {
        header: 'Series',
        meta: { tooltip: 'Series from database' },
        cell: (info) => (
          <span className="series-text" title={info.getValue()}>
            {info.getValue() || '-'}
          </span>
        ),
      }),
      ...(isFullAccessUser ? [
      columnHelper.accessor('file_type', {
        header: 'Type',
        meta: { tooltip: 'File type from database' },
        cell: (info) => {
          const value = info.getValue();
          return <span className="file-type-text" title={value}>{value || '-'}</span>;
        },
      }),
      columnHelper.accessor('audio_included', {
        header: 'Audio In',
        meta: { tooltip: 'Audio included status from database' },
        cell: (info) => {
          const value = info.getValue();
          return <span className="audio-included-text" title={value}>{value || '-'}</span>;
        },
      }),
      columnHelper.accessor('audio_ai_ready', {
        header: 'AI Audio',
        meta: { tooltip: 'Audio AI ready status from database' },
        cell: (info) => {
          const value = info.getValue();
          return <span className="audio-ai-ready-text" title={value}>{value || '-'}</span>;
        },
      }),
      columnHelper.accessor((row) => row.quiz_question_count, {
        id: 'quiz_question_count',
        header: 'Quizzes',
        meta: { tooltip: 'Quiz question count from database' },
        cell: (info) => {
          const value = info.row.original.quiz_question_count;
          const display = value != null && String(value).trim() !== '' ? String(value) : '-';
          return <span className="quiz-question-count-text" title={display}>{display}</span>;
        },
      }),
      ] : []),
      ...(isFullAccessUser ? [
      columnHelper.display({
        id: 'include_list_consumer',
        header: 'Consumer',
        meta: { tooltip: 'In age_appropriate_epubs_with_covers list' },
        enableSorting: false,
        cell: (info) => {
          const v = info.row.original.include_list_consumer;
          const value = v === true ? 'true' : v === false ? 'false' : '-';
          return <span className="include-list-consumer-text">{value}</span>;
        },
      }),
      columnHelper.display({
        id: 'include_list_educator',
        header: 'Educator',
        meta: { tooltip: 'In edu_isbns list' },
        enableSorting: false,
        cell: (info) => {
          const v = info.row.original.include_list_educator;
          const value = v === true ? 'true' : v === false ? 'false' : '-';
          return <span className="include-list-educator-text">{value}</span>;
        },
      }),
      ] : []),
      columnHelper.accessor('format', {
        header: 'Format',
        meta: { tooltip: 'Format from all-books (e.g. fixed)' },
        cell: (info) => {
          if (info.row.original.in_all_books === false) return <span />;
          const value = info.getValue();
          return <span className="format-text" title={value}>{value || '-'}</span>;
        },
      }),
      ...(isFullAccessUser ? [
      columnHelper.accessor('extracted_from', {
        header: 'Extracted From',
        meta: { tooltip: 'Source format from all-books (e.g. epub, pdf)' },
        cell: (info) => {
          if (info.row.original.in_all_books === false) return <span />;
          const value = info.getValue();
          return <span className="extracted-from-text" title={value}>{value || '-'}</span>;
        },
      }),
      columnHelper.accessor('audio_inside', {
        header: 'Audio Inside',
        id: 'audio_inside',
        meta: { tooltip: 'Audio inside from all-books' },
        cell: (info) => {
          if (info.row.original.in_all_books === false) return <span />;
          const value = info.getValue();
          if (value === true) return <span className="audio-inside-text">Yes</span>;
          if (value === false) return <span className="audio-inside-text">No</span>;
          return <span className="audio-inside-text">-</span>;
        },
      }),
      ] : []),
      ...(isFullAccessUser ? [
      columnHelper.display({
        id: 'good_audio',
        header: 'Good Audio',
        meta: { tooltip: 'Good audio (lists/good-audio.txt). "-" when list missing.' },
        enableSorting: false,
        cell: (info) => {
          if (info.row.original.in_all_books === false) return <span />;
          const v = info.row.original.good_audio;
          const value = v === true ? 'true' : v === false ? 'false' : '-';
          return <span className="good-audio-text">{value}</span>;
        },
      }),
      ] : []),
      columnHelper.accessor('page_count', {
        header: 'Pages',
        meta: { tooltip: 'Number of pages' },
        cell: (info) => {
          if (info.row.original.in_all_books === false) return <span className="page-count" />;
          const v = info.getValue();
          return <span className="page-count">{v || 'N/A'}</span>;
        },
      }),
      columnHelper.accessor('audio_service', {
        header: 'Service',
        meta: { tooltip: 'Audio service provider' },
        enableSorting: false,
        cell: (info) => {
          const value = info.getValue();
          const displayValue = value === 'elevenlabs' ? 'EL' : value === 'curioso-custom' ? 'custom' : (value || '-');
          return (
            <span className="audio-service-text" title={value}>
              {displayValue}
            </span>
          );
        },
      }),
      columnHelper.accessor('voice_name', {
        header: 'Voice',
        meta: { tooltip: 'Voice name used for TTS' },
        enableSorting: false,
        cell: (info) => {
          const value = info.getValue();
          const row = info.row.original;
          const audioService = row.audio_service;
          
          // Format display value for Azure voices
          let displayValue = value || '-';
          if (audioService === 'azure' && value && typeof value === 'string') {
            // Remove "en-US-" prefix if present
            if (value.startsWith('en-US-')) {
              displayValue = value.substring(6);
            }
            // Remove "Neural" suffix if present
            if (displayValue.endsWith('Neural')) {
              displayValue = displayValue.substring(0, displayValue.length - 6);
            }
          }
          
          return (
            <span className="voice-name-text" title={value || '-'}>
              {displayValue}
            </span>
          );
        },
      }),
      columnHelper.accessor('tts_chars', {
        header: 'Chars',
        meta: { tooltip: 'Total characters for TTS' },
        enableSorting: false,
        cell: (info) => {
          const value = info.getValue();
          if (value === null || value === undefined) {
            return <span className="chars-text">-</span>;
          }
          return (
            <span className="chars-text" title={`${value.toLocaleString()} characters`}>
              {value.toLocaleString()}
            </span>
          );
        },
      }),
      ...(isFullAccessUser ? [
      columnHelper.accessor('tts_cost_azure', {
        header: 'Azure cost',
        meta: { tooltip: 'Azure TTS cost' },
        enableSorting: false,
        cell: (info) => {
          const value = info.getValue();
          const row = info.row.original;
          const audioService = row.audio_service;
          
          // Determine if this cost should be greyed out
          const isActive = audioService === 'azure';
          const isInactive = audioService === 'elevenlabs';
          const isDefault = !audioService || audioService === '-' || (!isActive && !isInactive);
          
          if (value === null || value === undefined) {
            return <span className="azure-cost-text" style={{ color: isDefault ? 'inherit' : (isInactive ? '#999' : 'inherit') }}>-</span>;
          }
          
          const textColor = isDefault ? 'inherit' : (isInactive ? '#999' : 'inherit');
          
          return (
            <span 
              className="azure-cost-text" 
              title={`$${value.toFixed(2)}`}
              style={{ color: textColor }}
            >
              ${value.toFixed(2)}
            </span>
          );
        },
      }),
      columnHelper.accessor('tts_cost_elevenlabs', {
        header: 'EL cost',
        meta: { tooltip: 'ElevenLabs TTS cost' },
        enableSorting: false,
        cell: (info) => {
          const value = info.getValue();
          const row = info.row.original;
          const audioService = row.audio_service;
          
          // Determine if this cost should be greyed out
          const isActive = audioService === 'elevenlabs';
          const isInactive = audioService === 'azure';
          const isDefault = !audioService || audioService === '-' || (!isActive && !isInactive);
          
          if (value === null || value === undefined) {
            return <span className="el-cost-text" style={{ color: isDefault ? 'inherit' : (isInactive ? '#999' : 'inherit') }}>-</span>;
          }
          
          const textColor = isDefault ? 'inherit' : (isInactive ? '#999' : 'inherit');
          
          return (
            <span 
              className="el-cost-text" 
              title={`$${value.toFixed(2)}`}
              style={{ color: textColor }}
            >
              ${value.toFixed(2)}
            </span>
          );
        },
      }),
      ] : []),
      // Changed By column
      columnHelper.accessor('changed_by', {
        header: 'Changed By',
        meta: { tooltip: 'Users who made changes' },
        enableSorting: false,
        cell: (info) => {
          const rowIsbn = info.row.original.isbn;
          const value = info.getValue();
          if (!value || !Array.isArray(value) || value.length === 0) {
            return <span>-</span>;
          }
          console.log(`[BookTable] Rendering changed_by for ISBN ${rowIsbn}:`, value);
          // Strip @ and everything after it from each name
          const displayNames = value.map(name => {
            const atIndex = name.indexOf('@');
            return atIndex !== -1 ? name.substring(0, atIndex) : name;
          });
          const fullNames = value.join(', ');
          return (
            <span className="changed-by-text" title={fullNames}>
              {displayNames.join(', ')}
            </span>
          );
        },
      }),
      // Last Changed column
      columnHelper.accessor('last_changed', {
        header: 'Last Changed',
        meta: { tooltip: 'Last change timestamp' },
        enableSorting: false,
        cell: (info) => {
          const rowIsbn = info.row.original.isbn;
          const value = info.getValue();
          if (!value) {
            return <span>-</span>;
          }
          try {
            const date = new Date(value);
            const year = date.getFullYear().toString().slice(-2);
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const formatted = `${year}/${month}/${day} ${hours}:${minutes}`;
            console.log(`[BookTable] Rendering last_changed for ISBN ${rowIsbn}:`, value, '->', formatted);
            return (
              <span className="last-changed-text" title={date.toISOString()} style={{ fontSize: 'calc(1em - 2px)' }}>
                {formatted}
              </span>
            );
          } catch (e) {
            return <span>-</span>;
          }
        },
      }),
    ],
    [handleOpenBook, queuedBooks, syncResults, syncStatusUpdates, getEffectiveApprovalImages, isFullAccessUser]
  );

  // Get default column order from columns (must be after columns is defined)
  const defaultColumnOrder = useMemo(() => {
    return columns.map(col => col.id || '');
  }, [columns]);

  // Check if column order has been customized
  const hasCustomColumnOrder = useMemo(() => {
    if (!columnOrder || columnOrder.length === 0) return false;
    if (columnOrder.length !== defaultColumnOrder.length) {
      console.log('[BookTable] Column order length mismatch:', columnOrder.length, 'vs', defaultColumnOrder.length);
      return true;
    }
    const isCustom = columnOrder.some((id, index) => id !== defaultColumnOrder[index]);
    if (isCustom) {
      console.log('[BookTable] Column order is customized:', columnOrder, 'vs default:', defaultColumnOrder);
    }
    return isCustom;
  }, [columnOrder, defaultColumnOrder]);

  // Notify parent when column order changes
  useEffect(() => {
    if (onColumnOrderChange) {
      onColumnOrderChange(hasCustomColumnOrder);
    }
  }, [hasCustomColumnOrder, onColumnOrderChange]);

  // Memoize the table data to prevent unnecessary re-renders and crashes
  // Create a stable key from filteredBooks to detect actual book list changes
  const filteredBooksKey = useMemo(() => 
    filteredBooks.map(b => b.isbn).sort().join(',') + `|${filteredBooks.length}`,
    [filteredBooks]
  );
  
  // Store previous table data to reuse when only metadata changes
  const previousTableDataRef = useRef<any[]>([]);
  const previousBooksKeyRef = useRef<string>('');
  
  const tableData = useMemo(() => {
    try {
      // If the book list (ISBNs) hasn't changed, update metadata; use filteredBooks as base so approval updates (e.g. from onSyncCompleted) are included
      if (previousBooksKeyRef.current === filteredBooksKey && previousTableDataRef.current.length > 0) {
        const updated = filteredBooks.map((book, index) => {
          const metadata = spineMetadata.get(book.isbn);
          if (metadata) {
            const prevBook = previousTableDataRef.current[index];
            const metadataChanged = !prevBook ||
              prevBook.audio_service !== metadata.audio_service ||
              prevBook.voice_name !== metadata.voice_name ||
              prevBook.tts_chars !== metadata.tts_chars ||
              prevBook.tts_cost_azure !== metadata.tts_cost_azure ||
              prevBook.tts_cost_elevenlabs !== metadata.tts_cost_elevenlabs ||
              JSON.stringify(prevBook.changed_by) !== JSON.stringify(metadata.changed_by) ||
              prevBook.last_changed !== metadata.last_changed ||
              prevBook.fast_track !== metadata.fast_track ||
              prevBook.copyright !== metadata.copyright;
            if (metadataChanged) {
              return {
                ...book,
                audio_service: metadata.audio_service,
                voice_name: metadata.voice_name,
                tts_chars: metadata.tts_chars,
                tts_cost_azure: metadata.tts_cost_azure,
                tts_cost_elevenlabs: metadata.tts_cost_elevenlabs,
                changed_by: metadata.changed_by,
                last_changed: metadata.last_changed,
                fast_track: metadata.fast_track,
                copyright: metadata.copyright,
              };
            }
          }
          if (book.copyright === undefined) {
            return { ...book, copyright: false };
          }
          return book;
        });
        
        // Only return new array if something actually changed
        const hasChanges = updated.some((book, index) => book !== previousTableDataRef.current[index]);
        if (!hasChanges) {
          console.log('[BookTable] Table data unchanged, reusing previous array reference');
          return previousTableDataRef.current;
        }
        
        console.log('[BookTable] Metadata updated, creating new array with updated objects');
        previousTableDataRef.current = updated;
        return updated;
      }
      
      // Book list changed, create new array
      console.log(`[BookTable] Book list changed (key: ${filteredBooksKey}), creating new table data`);
      const newData = filteredBooks.map(book => {
        const metadata = spineMetadata.get(book.isbn);
        if (metadata) {
          return {
            ...book,
            audio_service: metadata.audio_service,
            voice_name: metadata.voice_name,
            tts_chars: metadata.tts_chars,
            tts_cost_azure: metadata.tts_cost_azure,
            tts_cost_elevenlabs: metadata.tts_cost_elevenlabs,
            changed_by: metadata.changed_by,
            last_changed: metadata.last_changed,
            fast_track: metadata.fast_track,
            copyright: metadata.copyright,
          };
        }
        // Ensure copyright field exists even if metadata not loaded
        if (book.copyright === undefined) {
          return { ...book, copyright: false };
        }
        return book;
      });
      
      previousTableDataRef.current = newData;
      previousBooksKeyRef.current = filteredBooksKey;
      return newData;
    } catch (error) {
      console.error('[BookTable] Error preparing table data:', error);
      // Return filteredBooks as fallback to prevent crash
      return filteredBooks;
    }
  }, [filteredBooksKey, filteredBooks, spineMetadata]);

  // Dispatch selection change events (with dbSyncEligibleCount for header DB Sync button)
  useEffect(() => {
    const selectedCount = Object.keys(rowSelection).length;
    const selectedBooks = tableData.filter((b) => rowSelection[b.isbn]);
    const dbSyncEligibleCount = selectedBooks.filter(isBookEligibleForDbSync).length;
    const event = new CustomEvent('bookSelectionChanged', {
      detail: { count: selectedCount, dbSyncEligibleCount },
    });
    window.dispatchEvent(event);
  }, [rowSelection, tableData]);

  // Listen for DB Sync: selected rows (header "DB Sync (x)" when x > 0) or bulk paste (when x === 0)
  useEffect(() => {
    const handleDbSyncSelected = async () => {
      const selectedBooks = tableData.filter((b) => rowSelection[b.isbn]);
      if (selectedBooks.length === 0) return;
      const { payload, ineligibleList } = buildDbSyncPayload(selectedBooks);
      if (payload.updates.length === 0) {
        const { toast } = await import('sonner');
        const skipped = ineligibleList.map((x) => x.title || x.isbn).join(', ');
        toast.warning(`No selected books are eligible for DB sync. Skipped: ${skipped || '—'}`);
        return;
      }
      setDbSyncModal({ payload, ineligibleList: ineligibleList.length > 0 ? ineligibleList : undefined });
    };

    const handleDbSyncBulkIsbns = async (event: Event) => {
      const e = event as CustomEvent<{ isbns: string[] }>;
      const isbns = e.detail?.isbns;
      if (!isbns?.length) return;
      const isbnSet = new Set(isbns);
      const booksInList = tableData.filter((b) => isbnSet.has(b.isbn));
      const notInList = isbns.filter((isbn) => !tableData.some((b) => b.isbn === isbn));
      const { payload, ineligibleList } = buildDbSyncPayload(booksInList);
      const ineligibleListWithMissing = notInList.length > 0
        ? [...ineligibleList, ...notInList.map((isbn) => ({ isbn, title: undefined as string | undefined }))]
        : ineligibleList;
      if (payload.updates.length === 0) {
        const { toast } = await import('sonner');
        const skipped = ineligibleListWithMissing.map((x) => x.title || x.isbn).join(', ');
        toast.warning(`No pasted books are eligible for DB sync (must be in current list). Skipped: ${skipped || '—'}`);
        return;
      }
      setDbSyncModal({
        payload,
        ineligibleList: ineligibleListWithMissing.length > 0 ? ineligibleListWithMissing : undefined,
      });
    };

    window.addEventListener('dbSyncSelectedBooks', handleDbSyncSelected);
    window.addEventListener('dbSyncBulkIsbns', handleDbSyncBulkIsbns);
    return () => {
      window.removeEventListener('dbSyncSelectedBooks', handleDbSyncSelected);
      window.removeEventListener('dbSyncBulkIsbns', handleDbSyncBulkIsbns);
    };
  }, [tableData, rowSelection]);

  const table = useReactTable({
    data: tableData,
    columns,
    getRowId: (row) => row.isbn,
    enableRowSelection: (row) => row.original.in_all_books !== false,
    state: {
      sorting,
      columnFilters,
      pagination,
      columnOrder: columnOrder.length > 0 ? columnOrder : undefined,
      rowSelection,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: (updater) => {
      setColumnFilters(updater);
      // Reset selection when filters change
      setRowSelection({});
    },
    onColumnOrderChange: (updater) => {
      setColumnOrder(prev => {
        const newOrder = typeof updater === 'function' ? updater(prev) : updater;
        console.log('[BookTable] Column order changed:', newOrder);
        return newOrder;
      });
    },
    onRowSelectionChange: setRowSelection,
    onPaginationChange: (updater) => {
      setPagination(prev => {
        const newPagination = typeof updater === 'function' ? updater(prev) : updater;
        
        // Reset selection when pagination changes
        setRowSelection({});
        
        // If this is user-initiated navigation, always allow it (including going to page 0)
        if (isUserNavigationRef.current) {
          console.log(`[BookTable] User navigation: ${prev.pageIndex} -> ${newPagination.pageIndex}`);
          // Keep flag set longer to handle multiple React Table calls in the same render cycle
          setTimeout(() => {
            isUserNavigationRef.current = false;
          }, 100);
          return newPagination;
        }
        
        // If React Table is trying to auto-reset to 0 due to data changes, prevent it if current page is valid
        // Only block if going TO 0 FROM a higher page, and the current page is still valid
        // Allow backward navigation by exactly 1 page (normal prev button behavior)
        if (newPagination.pageIndex === 0 && prev.pageIndex > 0) {
          const maxPageIndex = Math.max(0, Math.ceil(tableData.length / pagination.pageSize) - 1);
          const isBackwardNavigation = newPagination.pageIndex === prev.pageIndex - 1;
          // Only prevent if it's NOT a normal backward navigation AND current page is still valid
          if (prev.pageIndex <= maxPageIndex && !isBackwardNavigation) {
            console.log(`[BookTable] Preventing auto-reset from page ${prev.pageIndex} to 0, staying on page ${prev.pageIndex}`);
            return { ...newPagination, pageIndex: prev.pageIndex };
          }
        }
        
        console.log(`[BookTable] React Table pagination change: ${prev.pageIndex} -> ${newPagination.pageIndex}`);
        return newPagination;
      });
    },
    // Disable auto-reset of page index when data changes
    autoResetPageIndex: false,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: false,
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
    defaultColumn: {},
  });

  // Report filtered+sorted list and pagination for pages table sync (same filters, order, and first row)
  useEffect(() => {
    if (!onFilteredSortedStateChange) return;
    const rows = table.getFilteredRowModel().rows;
    const books = rows.map((r) => r.original);
    const { pageIndex, pageSize } = table.getState().pagination;
    onFilteredSortedStateChange(books, pageIndex, pageSize);
  }, [table, onFilteredSortedStateChange, columnFilters, sorting, pagination.pageIndex, pagination.pageSize, tableData]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight' && table.getCanNextPage()) {
        event.preventDefault();
        const newIndex = table.getState().pagination.pageIndex + 1;
        isUserNavigationRef.current = true;
        table.setPageIndex(newIndex);
        settingsService.setInfoTablePageIndex(newIndex);
      } else if (event.key === 'ArrowLeft' && table.getCanPreviousPage()) {
        event.preventDefault();
        const newIndex = table.getState().pagination.pageIndex - 1;
        isUserNavigationRef.current = true;
        table.setPageIndex(newIndex);
        settingsService.setInfoTablePageIndex(newIndex);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [table]);

  // Sync horizontal scroll between top scrollbar and table
  useEffect(() => {
    const tableScroll = tableScrollRef.current;
    const topScrollbar = topScrollbarRef.current;
    const spacer = scrollbarSpacerRef.current;

    if (!tableScroll || !topScrollbar || !spacer) return;

    // Update spacer width to match table width for scrollbar sizing
    const updateSpacerWidth = () => {
      const table = tableScroll.querySelector('table');
      if (table) {
        spacer.style.width = `${table.scrollWidth}px`;
        
        // Show/hide top scrollbar based on whether table overflows
        const hasHorizontalScroll = table.scrollWidth > tableScroll.clientWidth;
        topScrollbar.style.display = hasHorizontalScroll ? 'block' : 'none';
      }
    };

    // Sync scroll from table to top scrollbar
    const handleTableScroll = () => {
      if (topScrollbar.scrollLeft !== tableScroll.scrollLeft) {
        topScrollbar.scrollLeft = tableScroll.scrollLeft;
      }
    };

    // Sync scroll from top scrollbar to table
    const handleTopScrollbarScroll = () => {
      if (tableScroll.scrollLeft !== topScrollbar.scrollLeft) {
        tableScroll.scrollLeft = topScrollbar.scrollLeft;
      }
    };

    // Initial width update
    updateSpacerWidth();

    // Update width when table content changes
    const resizeObserver = new ResizeObserver(() => {
      updateSpacerWidth();
    });

    const table = tableScroll.querySelector('table');
    if (table) {
      resizeObserver.observe(table);
    }

    // Also observe the container for size changes
    resizeObserver.observe(tableScroll);

    tableScroll.addEventListener('scroll', handleTableScroll);
    topScrollbar.addEventListener('scroll', handleTopScrollbarScroll);

    return () => {
      tableScroll.removeEventListener('scroll', handleTableScroll);
      topScrollbar.removeEventListener('scroll', handleTopScrollbarScroll);
      resizeObserver.disconnect();
    };
  }, [tableData]);

  const copyIsbnsText = filteredBooks.map(b => b.isbn).join('\n');

  return (
    <>
    <div className="books-table">
      <div className="table-scroll-wrapper">
        <div 
          ref={topScrollbarRef}
          className="table-scrollbar-top"
        >
          <div ref={scrollbarSpacerRef} className="table-scrollbar-spacer" />
        </div>
        <div ref={tableScrollRef} className="table-responsive">
        <table>
          <thead>
            {/* Second header row with colspans for grouping */}
            <tr>
              {(() => {
                const headers = table.getHeaderGroups()[0]?.headers || [];
                const titleIndex = headers.findIndex(h => h.id === 'book_title');
                const audioAiReadyIndex = headers.findIndex(h => h.id === 'audio_ai_ready');
                const quizzesIndex = headers.findIndex(h => h.id === 'quiz_question_count');
                const pagesIndex = headers.findIndex(h => h.id === 'page_count');
                const inDbIndex = headers.findIndex(h => h.id === 'in_db');
                const imagesCreatedIndex = headers.findIndex(h => h.id === 'images_created');
                const textExtractedIndex = headers.findIndex(h => h.id === 'text_extracted');
                const approvalImagesIndex = headers.findIndex(h => h.id === 'approval_images');
                const copyrightIndex = headers.findIndex(h => h.id === 'copyright');
                const syncIndex = headers.findIndex(h => h.id === 'production_file_sync');
                const totalCols = headers.length;
                
                // Left group: ISBN, Prod (2 columns)
                const leftSpan = inDbIndex >= 0 ? inDbIndex : (imagesCreatedIndex >= 0 ? imagesCreatedIndex : 2);
                // Conversion: in_db, images_created, text_extracted (3 columns)
                const conversionSpan = textExtractedIndex >= 0 && inDbIndex >= 0 ? textExtractedIndex - inDbIndex + 1 : 3;
                // Review: approval_images, approval_words, approval_voice, fast_track, copyright (5 columns)
                const reviewSpan = copyrightIndex >= 0 && approvalImagesIndex >= 0 ? copyrightIndex - approvalImagesIndex + 1 : 5;
                // Files group: production_file_sync_status, production_file_sync (2 cols when upload available)
                const syncStatusIndex = headers.findIndex(h => h.id === 'production_file_sync_status');
                const filesGroupSpan = (syncStatusIndex >= 0 ? 1 : 0) + (syncIndex >= 0 ? 1 : 0);
                // DB group: production_file_sync_db_status, production_file_sync_db (2 cols)
                const syncDbStatusIndex = headers.findIndex(h => h.id === 'production_file_sync_db_status');
                const syncDbIndex = headers.findIndex(h => h.id === 'production_file_sync_db');
                const dbGroupSpan = (syncDbStatusIndex >= 0 ? 1 : 0) + (syncDbIndex >= 0 ? 1 : 0);
                // DB Values: book_title through Quizzes (or through series when quiz column hidden e.g. Kriyadocs)
                const seriesIndex = headers.findIndex(h => h.id === 'series');
                const dbValuesEndIndex = quizzesIndex >= 0 ? quizzesIndex : seriesIndex;
                const dbValuesSpan = titleIndex >= 0 && dbValuesEndIndex >= 0 ? dbValuesEndIndex - titleIndex + 1 : 0;
                // Include Lists: Consumer, Educator (2 columns)
                const includeListsSpan = 2;
                // Latest Book Admin Files: format, extracted_from, audio_inside, page_count through the end
                const formatIndex = headers.findIndex(h => h.id === 'format');
                const latestFilesSpan = formatIndex >= 0 ? totalCols - formatIndex : (pagesIndex >= 0 ? totalCols - pagesIndex : 0);
                
                return (
                  <>
                    {leftSpan > 0 && (
                      <th 
                        colSpan={leftSpan} 
                        style={{ 
                          background: 'var(--table-header-bg)', 
                          border: '1px solid var(--border-primary)',
                          padding: '4px'
                        }}
                      ></th>
                    )}
                    {conversionSpan > 0 && (
                      <th 
                        colSpan={conversionSpan} 
                        style={{ 
                          background: 'var(--table-header-bg)', 
                          color: '#b9e9b9',
                          textAlign: 'center',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          padding: '4px',
                          border: '1px solid var(--border-primary)'
                        }}
                      >
                        Conversion
                      </th>
                    )}
                    {reviewSpan > 0 && (
                      <th 
                        colSpan={reviewSpan} 
                        style={{ 
                          background: 'var(--table-header-bg)', 
                          color: '#b9e9b9',
                          textAlign: 'center',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          padding: '4px',
                          border: '1px solid var(--border-primary)'
                        }}
                      >
                        Review
                      </th>
                    )}
                    {isFullAccessUser && filesGroupSpan > 0 && (
                      <th 
                        colSpan={filesGroupSpan} 
                        style={{ 
                          background: 'var(--table-header-bg)', 
                          color: '#b9e9b9',
                          textAlign: 'center',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          border: '1px solid var(--border-primary)',
                          padding: '4px'
                        }}
                      >
                        Files
                      </th>
                    )}
                    {isFullAccessUser && dbGroupSpan > 0 && (
                      <th 
                        colSpan={dbGroupSpan} 
                        style={{ 
                          background: 'var(--table-header-bg)', 
                          color: '#b9e9b9',
                          textAlign: 'center',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          border: '1px solid var(--border-primary)',
                          padding: '4px'
                        }}
                      >
                        DB
                      </th>
                    )}
                    {dbValuesSpan > 0 && (
                      <th 
                        colSpan={dbValuesSpan} 
                        style={{ 
                          background: 'var(--table-header-bg)', 
                          color: '#b2d2f6',
                          textAlign: 'center',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          padding: '4px',
                          border: '1px solid var(--border-primary)'
                        }}
                      >
                        DB Values
                      </th>
                    )}
                    {isFullAccessUser && includeListsSpan > 0 && (
                      <th 
                        colSpan={includeListsSpan} 
                        style={{ 
                          background: 'var(--table-header-bg)', 
                          color: '#b9e9b9',
                          textAlign: 'center',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          padding: '4px',
                          border: '1px solid var(--border-primary)'
                        }}
                      >
                        Include Lists
                      </th>
                    )}
                    {latestFilesSpan > 0 && (
                      <th 
                        colSpan={latestFilesSpan} 
                        style={{ 
                          background: 'var(--table-header-bg)', 
                          color: '#b9e9b9',
                          textAlign: 'center',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          padding: '4px',
                          border: '1px solid var(--border-primary)'
                        }}
                      >
                        Latest Book Admin Files
                      </th>
                    )}
                  </>
                );
              })()}
            </tr>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  // DB columns: book_title, publisher, series, file_type, audio_included, audio_ai_ready, quiz_question_count
                  const isDbColumn = ['book_title', 'publisher', 'series', 'file_type', 'audio_included', 'audio_ai_ready', 'quiz_question_count'].includes(header.id);
                  const className = [
                    header.id === 'possibleIssuesInfo' ? 'possible-issues-info-column' : '',
                    isDbColumn ? 'db-column-header' : 'file-column-header'
                  ].filter(Boolean).join(' ');
                  
                  return (
                  <th 
                    key={header.id}
                    data-column-id={header.id}
                    className={className}
                    title={header.column.columnDef.meta?.tooltip}
                    style={{ 
                      cursor: 'default',
                    }}
                  >
                    <div className="book-table-header-cell-inner">
                      <div>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      </div>
                      <div className="book-table-header-icons">
                      {header.column.getCanSort() && (
                          <span 
                            style={{ 
                              fontSize: '12px',
                              cursor: 'pointer',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              header.column.getToggleSortingHandler()?.(e);
                            }}
                          >
                          {{
                            asc: '↑',
                            desc: '↓',
                          }[header.column.getIsSorted() as string] ?? '↕'}
                        </span>
                      )}
                      {/* Filter icon for filterable columns */}
                        {['publisher', 'series', 'text_extracted', 'approval', 'approval_images', 'approval_words', 'approval_voice', 'file_type', 'audio_included', 'audio_ai_ready', 'quiz_question_count', 'in_db', 'images_created', 'fast_track', 'copyright', 'production_file_sync_status', 'production_file_sync_db_status', 'include_list_consumer', 'include_list_educator', 'format', 'extracted_from', 'audio_service', 'voice_name', 'audio_inside', 'good_audio'].includes(header.column.id) && (
                        <div style={{ position: 'relative' }}>
                          <button
                            type="button"
                            className={`book-table-filter-btn ${isFilterActive(header.column.id) ? 'active' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenFilterDropdown(openFilterDropdown === header.column.id ? null : header.column.id);
                            }}
                            title={isFilterActive(header.column.id) ? 'Filter active' : 'Filter'}
                          >
                            {isFilterActive(header.column.id) ? '▼' : '▼'}
                          </button>
                          
                          {/* Filter dropdown */}
                          {openFilterDropdown === header.column.id && (
                            <div
                              ref={(el) => { filterDropdownRefs.current[header.column.id] = el; }}
                              style={{
                                position: 'absolute',
                                top: 'calc(100% + 1px)',
                                left: 0,
                                zIndex: 1000,
                                backgroundColor: 'var(--bg-primary)',
                                color: 'var(--text-primary)',
                                border: '1px solid var(--border-primary)',
                                borderRadius: '4px',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                minWidth: '200px',
                                maxHeight: '300px',
                                overflowY: 'auto',
                                padding: '8px'
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <span style={{ fontSize: '12px', fontWeight: 'bold' }}>Filter by {header.column.id}</span>
                                <button
                                  onClick={() => clearFilter(header.column.id)}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '10px',
                                    color: 'var(--text-secondary)'
                                  }}
                                >
                                  Clear
                                </button>
                              </div>
                              {filterOptions[header.column.id]?.map((option) => {
                                // Get books filtered by all filters EXCEPT the current column's filter
                                // Also apply search/columnFilters
                                // Note: 'books' prop is already filtered by the selected list (from BookList)
                                const booksForCount = books.filter(book => {
                                  // Apply all activeFilters except the current column
                                  if (activeFilters.publisher && activeFilters.publisher.length > 0 && header.column.id !== 'publisher') {
                                    if (!activeFilters.publisher.includes(book.publisher || '')) return false;
                                  }
                                  if (activeFilters.series && activeFilters.series.length > 0 && header.column.id !== 'series') {
                                    if (!activeFilters.series.includes(book.series || '')) return false;
                                  }
                                  if (activeFilters.text_extracted && activeFilters.text_extracted.length > 0 && header.column.id !== 'text_extracted') {
                                    const textValue = book.text_extracted || '';
                                    const matches = activeFilters.text_extracted.some(filterValue => {
                                      if (filterValue === '') {
                                        return textValue === '' || textValue === null || textValue === undefined;
                                      }
                                      return textValue === filterValue;
                                    });
                                    if (!matches) return false;
                                  }
                                  if (activeFilters.file_type && activeFilters.file_type.length > 0 && header.column.id !== 'file_type') {
                                    if (!activeFilters.file_type.includes(book.file_type || '')) return false;
                                  }
                                  if (activeFilters.audio_included && activeFilters.audio_included.length > 0 && header.column.id !== 'audio_included') {
                                    if (!activeFilters.audio_included.includes(book.audio_included || '')) return false;
                                  }
                                  if (activeFilters.audio_ai_ready && activeFilters.audio_ai_ready.length > 0 && header.column.id !== 'audio_ai_ready') {
                                    if (!activeFilters.audio_ai_ready.includes(book.audio_ai_ready || '')) return false;
                                  }
                                  if (activeFilters.quiz_question_count && activeFilters.quiz_question_count.length > 0 && header.column.id !== 'quiz_question_count') {
                                    if (!activeFilters.quiz_question_count.includes(book.quiz_question_count || '')) return false;
                                  }
                                  if (activeFilters.in_db && activeFilters.in_db.length > 0 && header.column.id !== 'in_db') {
                                    const inDb = book.in_db === true ? 'true' : 'false';
                                    if (!activeFilters.in_db.includes(inDb)) return false;
                                  }
                                  // Images Created filter
                                  if (activeFilters.images_created && activeFilters.images_created.length > 0 && header.column.id !== 'images_created') {
                                    const imagesCreated = book.images_created === true ? 'true' : 'false';
                                    if (!activeFilters.images_created.includes(imagesCreated)) return false;
                                  }
                                  // Fast Track filter
                                  if (activeFilters.fast_track && activeFilters.fast_track.length > 0 && header.column.id !== 'fast_track') {
                                    const fastTrack = book.fast_track === true ? 'true' : book.fast_track === false ? 'false' : 'null';
                                    if (!activeFilters.fast_track.includes(fastTrack)) return false;
                                  }
                                  // Copyright filter
                                  if (activeFilters.copyright && activeFilters.copyright.length > 0 && header.column.id !== 'copyright') {
                                    const copyright = book.copyright === true ? 'true' : book.copyright === false ? 'false' : 'null';
                                    if (!activeFilters.copyright.includes(copyright)) return false;
                                  }
                                  // Production File Sync Status filter (use effective status)
                                  if (activeFilters.production_file_sync_status && activeFilters.production_file_sync_status.length > 0 && header.column.id !== 'production_file_sync_status') {
                                    const status = getEffectiveSyncStatus(book);
                                    if (!activeFilters.production_file_sync_status.includes(status)) return false;
                                  }
                                  if (activeFilters.production_file_sync_db_status && activeFilters.production_file_sync_db_status.length > 0 && header.column.id !== 'production_file_sync_db_status') {
                                    if (!activeFilters.production_file_sync_db_status.includes(getDbSyncStatus(book))) return false;
                                  }
                                  if (activeFilters.format && activeFilters.format.length > 0 && header.column.id !== 'format') {
                                    const formatVal = (book.format && String(book.format).trim()) ? book.format : 'unknown';
                                    if (!activeFilters.format.includes(formatVal)) return false;
                                  }
                                  if (activeFilters.extracted_from && activeFilters.extracted_from.length > 0 && header.column.id !== 'extracted_from') {
                                    const ef = book.extracted_from;
                                    const extractedFromVal = (ef && String(ef).trim() && ef !== 'N/A') ? ef : 'unknown';
                                    if (!activeFilters.extracted_from.includes(extractedFromVal)) return false;
                                  }
                                  if (activeFilters.audio_service && activeFilters.audio_service.length > 0 && header.column.id !== 'audio_service') {
                                    const voiceService = book.voice_service ?? book.audio_service ?? '-';
                                    if (!activeFilters.audio_service.includes(voiceService)) return false;
                                  }
                                  if (activeFilters.voice_name && activeFilters.voice_name.length > 0 && header.column.id !== 'voice_name') {
                                    const voiceName = book.voice_name ?? '-';
                                    if (!activeFilters.voice_name.includes(voiceName)) return false;
                                  }
                                  if (activeFilters.audio_inside && activeFilters.audio_inside.length > 0 && header.column.id !== 'audio_inside') {
                                    const audioInside = book.audio_inside === true ? 'true' : book.audio_inside === false ? 'false' : 'null';
                                    if (!activeFilters.audio_inside.includes(audioInside)) return false;
                                  }
                                  if (activeFilters.good_audio && activeFilters.good_audio.length > 0 && header.column.id !== 'good_audio') {
                                    const goodAudio = book.good_audio === true ? 'true' : book.good_audio === false ? 'false' : '-';
                                    if (!activeFilters.good_audio.includes(goodAudio)) return false;
                                  }
                                  if (activeFilters.include_list_consumer && activeFilters.include_list_consumer.length > 0 && header.column.id !== 'include_list_consumer') {
                                    const consumer = book.include_list_consumer === true ? 'true' : book.include_list_consumer === false ? 'false' : '-';
                                    if (!activeFilters.include_list_consumer.includes(consumer)) return false;
                                  }
                                  if (activeFilters.include_list_educator && activeFilters.include_list_educator.length > 0 && header.column.id !== 'include_list_educator') {
                                    const educator = book.include_list_educator === true ? 'true' : book.include_list_educator === false ? 'false' : '-';
                                    if (!activeFilters.include_list_educator.includes(educator)) return false;
                                  }
                                  // Approval Images filter
                                  if (activeFilters.approval_images && activeFilters.approval_images.length > 0 && header.column.id !== 'approval_images') {
                                    const imagesValue = getEffectiveApprovalImages(book, book.isbn);
                                    const isNone = imagesValue === null || imagesValue === undefined || imagesValue === '';
                                    const isOther = !isNone && imagesValue !== true && imagesValue !== 'true' && imagesValue !== 'auto' && imagesValue !== false && imagesValue !== 'false';
                                    let matches = false;
                                    for (const filterValue of activeFilters.approval_images) {
                                      if (filterValue === 'None' && isNone) { matches = true; break; }
                                      if (filterValue === 'Other' && isOther) { matches = true; break; }
                                      if (filterValue === 'Approved' && (imagesValue === true || imagesValue === 'true')) { matches = true; break; }
                                      if (filterValue === 'Auto' && imagesValue === 'auto') { matches = true; break; }
                                      if (filterValue === 'Failed' && (imagesValue === false || imagesValue === 'false')) { matches = true; break; }
                                    }
                                    if (!matches) return false;
                                  }
                                  
                                  // Approval Words filter
                                  if (activeFilters.approval_words && activeFilters.approval_words.length > 0 && header.column.id !== 'approval_words') {
                                    const approval = book.approval;
                                    const wordsValue = approval?.words;
                                    const isNone = !approval || wordsValue === null || wordsValue === undefined || wordsValue === '';
                                    const isOther = !isNone && wordsValue !== true && wordsValue !== 'true' && wordsValue !== false && wordsValue !== 'false' && wordsValue !== 'auto';
                                    let matches = false;
                                    for (const filterValue of activeFilters.approval_words) {
                                      if (filterValue === 'None' && isNone) { matches = true; break; }
                                      if (filterValue === 'Other' && isOther) { matches = true; break; }
                                      if (filterValue === 'Approved' && (wordsValue === true || wordsValue === 'true')) { matches = true; break; }
                                      if (filterValue === 'Auto' && wordsValue === 'auto') { matches = true; break; }
                                      if (filterValue === 'Failed' && (wordsValue === false || wordsValue === 'false')) { matches = true; break; }
                                    }
                                    if (!matches) return false;
                                  }
                                  
                                  // Approval Voice filter
                                  if (activeFilters.approval_voice && activeFilters.approval_voice.length > 0 && header.column.id !== 'approval_voice') {
                                    const approval = book.approval;
                                    const voiceValue = approval?.voice;
                                    let matches = false;
                                    for (const filterValue of activeFilters.approval_voice) {
                                      if (filterValue === 'None' && (!approval || voiceValue === null || voiceValue === undefined || (voiceValue !== true && voiceValue !== 'true'))) {
                                        matches = true;
                                        break;
                                      } else if (filterValue === 'Added' && (voiceValue === true || voiceValue === 'true')) {
                                        matches = true;
                                        break;
                                      }
                                    }
                                    if (!matches) return false;
                                  }
                                  
                                  // Approval filter (legacy support)
                                  if (activeFilters.approval && activeFilters.approval.length > 0 && header.column.id !== 'approval') {
                                    const approval = book.approval;
                                    const effectiveImages = getEffectiveApprovalImages(book, book.isbn);
                                    const effectiveWords = getEffectiveApprovalWords(book, book.isbn);
                                    let approvalMatches = false;
                                    for (const filterValue of activeFilters.approval) {
                                      if (filterValue === 'None' && !approval) {
                                        approvalMatches = true;
                                        break;
                                      } else if (approval) {
                                        if (filterValue === 'Images Approved' && (effectiveImages === true || effectiveImages === 'true')) {
                                          approvalMatches = true;
                                          break;
                                        } else if (filterValue === 'Images Auto' && effectiveImages === 'auto') {
                                          approvalMatches = true;
                                          break;
                                        } else if (filterValue === 'Images Failed' && (effectiveImages === false || effectiveImages === 'false')) {
                                          approvalMatches = true;
                                          break;
                                        } else if (filterValue === 'Words Approved' && (effectiveWords === true || effectiveWords === 'true')) {
                                          approvalMatches = true;
                                          break;
                                        } else if (filterValue === 'Words Auto' && effectiveWords === 'auto') {
                                          approvalMatches = true;
                                          break;
                                        } else if (filterValue === 'Words Failed' && (effectiveWords === false || effectiveWords === 'false')) {
                                          approvalMatches = true;
                                          break;
                                        } else if (filterValue === 'Voice Added' && (approval.voice === true || approval.voice === 'true')) {
                                          approvalMatches = true;
                                          break;
                                        }
                                      }
                                    }
                                    if (!approvalMatches) return false;
                                  }
                                  
                                  // Apply search/columnFilters
                                  if (columnFilters.length > 0) {
                                    for (const filter of columnFilters) {
                                      const columnId = filter.id;
                                      const filterValue = filter.value as string;
                                      if (!filterValue) continue;
                                      
                                      const searchValue = filterValue.toLowerCase();
                                      let matches = false;
                                      
                                      if (columnId === 'isbn' && book.isbn?.toLowerCase().includes(searchValue)) {
                                        matches = true;
                                      } else if (columnId === 'book_title' && book.book_title?.toLowerCase().includes(searchValue)) {
                                        matches = true;
                                      } else if (columnId === 'publisher' && book.publisher?.toLowerCase().includes(searchValue)) {
                                        matches = true;
                                      } else if (columnId === 'series' && book.series?.toLowerCase().includes(searchValue)) {
                                        matches = true;
                                      }
                                      
                                      if (!matches) return false;
                                    }
                                  }
                                  
                                  return true;
                                });
                                
                                // Count books matching this option
                                let count = 0;
                                booksForCount.forEach(book => {
                                  let matches = false;
                                  
                                  if (header.column.id === 'publisher') {
                                    matches = (book.publisher || '') === option;
                                  } else if (header.column.id === 'series') {
                                    matches = (book.series || '') === option;
                                  } else if (header.column.id === 'text_extracted') {
                                    const textValue = book.text_extracted || '';
                                    if (option === '') {
                                      matches = textValue === '' || textValue === null || textValue === undefined;
                                    } else {
                                      matches = textValue === option;
                                    }
                                  } else if (header.column.id === 'images_created') {
                                    matches = (book.images_created === true ? 'true' : 'false') === option;
                                  } else if (header.column.id === 'fast_track') {
                                    const fastTrack = book.fast_track === true ? 'true' : book.fast_track === false ? 'false' : 'null';
                                    matches = fastTrack === option;
                                  } else if (header.column.id === 'copyright') {
                                    const copyright = book.copyright === true ? 'true' : book.copyright === false ? 'false' : 'null';
                                    matches = copyright === option;
                                  } else if (header.column.id === 'production_file_sync_status') {
                                    const status = getEffectiveSyncStatus(book);
                                    matches = status === option;
                                  } else if (header.column.id === 'production_file_sync_db_status') {
                                    matches = getDbSyncStatus(book) === option;
                                  } else if (header.column.id === 'file_type') {
                                    matches = (book.file_type || '') === option;
                                  } else if (header.column.id === 'audio_included') {
                                    matches = (book.audio_included || '') === option;
                                  } else if (header.column.id === 'audio_ai_ready') {
                                    matches = (book.audio_ai_ready || '') === option;
                                  } else if (header.column.id === 'quiz_question_count') {
                                    matches = (book.quiz_question_count || '') === option;
                                  } else if (header.column.id === 'in_db') {
                                    const inDb = book.in_db === true ? 'true' : 'false';
                                    matches = inDb === option;
                                  } else if (header.column.id === 'format') {
                                    const formatVal = (book.format && String(book.format).trim()) ? book.format : 'unknown';
                                    matches = formatVal === option;
                                  } else if (header.column.id === 'extracted_from') {
                                    const ef = book.extracted_from;
                                    const extractedFromVal = (ef && String(ef).trim() && ef !== 'N/A') ? ef : 'unknown';
                                    matches = extractedFromVal === option;
                                  } else if (header.column.id === 'audio_service') {
                                    const voiceService = book.voice_service ?? book.audio_service ?? '-';
                                    matches = voiceService === option;
                                  } else if (header.column.id === 'voice_name') {
                                    const voiceName = book.voice_name ?? '-';
                                    matches = voiceName === option;
                                  } else if (header.column.id === 'audio_inside') {
                                    const audioInside = book.audio_inside === true ? 'true' : book.audio_inside === false ? 'false' : 'null';
                                    matches = audioInside === option;
                                  } else if (header.column.id === 'good_audio') {
                                    const goodAudio = book.good_audio === true ? 'true' : book.good_audio === false ? 'false' : '-';
                                    matches = goodAudio === option;
                                  } else if (header.column.id === 'include_list_consumer') {
                                    const consumer = book.include_list_consumer === true ? 'true' : book.include_list_consumer === false ? 'false' : '-';
                                    matches = consumer === option;
                                  } else if (header.column.id === 'include_list_educator') {
                                    const educator = book.include_list_educator === true ? 'true' : book.include_list_educator === false ? 'false' : '-';
                                    matches = educator === option;
                                  } else if (header.column.id === 'approval_images') {
                                    const imagesValue = getEffectiveApprovalImages(book, book.isbn);
                                    const isNone = imagesValue === null || imagesValue === undefined || imagesValue === '';
                                    const isOther = !isNone && imagesValue !== true && imagesValue !== 'true' && imagesValue !== 'auto' && imagesValue !== false && imagesValue !== 'false';
                                    if (option === 'None' && isNone) matches = true;
                                    else if (option === 'Other' && isOther) matches = true;
                                    else if (option === 'Approved' && (imagesValue === true || imagesValue === 'true')) matches = true;
                                    else if (option === 'Auto' && imagesValue === 'auto') matches = true;
                                    else if (option === 'Failed' && (imagesValue === false || imagesValue === 'false')) matches = true;
                                  } else if (header.column.id === 'approval_words') {
                                    const wordsValue = getEffectiveApprovalWords(book, book.isbn);
                                    const isNone = wordsValue === null || wordsValue === undefined || wordsValue === '';
                                    const isOther = !isNone && wordsValue !== true && wordsValue !== 'true' && wordsValue !== false && wordsValue !== 'false' && wordsValue !== 'auto';
                                    if (option === 'None' && isNone) matches = true;
                                    else if (option === 'Other' && isOther) matches = true;
                                    else if (option === 'Approved' && (wordsValue === true || wordsValue === 'true')) matches = true;
                                    else if (option === 'Auto' && wordsValue === 'auto') matches = true;
                                    else if (option === 'Failed' && (wordsValue === false || wordsValue === 'false')) matches = true;
                                  } else if (header.column.id === 'approval_voice') {
                                    const approval = book.approval;
                                    const voiceValue = approval?.voice;
                                    const isNone = !approval || voiceValue === null || voiceValue === undefined || voiceValue === '';
                                    const isOther = !isNone && voiceValue !== true && voiceValue !== 'true';
                                    if (option === 'None' && isNone) matches = true;
                                    else if (option === 'Other' && isOther) matches = true;
                                    else if (option === 'Added' && (voiceValue === true || voiceValue === 'true')) matches = true;
                                  } else if (header.column.id === 'approval') {
                                    const approval = book.approval;
                                    const effectiveImages = getEffectiveApprovalImages(book, book.isbn);
                                    const effectiveWords = getEffectiveApprovalWords(book, book.isbn);
                                    const matchesAnySpecific = approval && (
                                      (effectiveImages === true || effectiveImages === 'true' || effectiveImages === 'auto' || effectiveImages === false || effectiveImages === 'false') ||
                                      (effectiveWords === true || effectiveWords === 'true' || effectiveWords === 'auto' || effectiveWords === false || effectiveWords === 'false') ||
                                      (approval.voice === true || approval.voice === 'true')
                                    );
                                    if (option === 'None' && !approval) matches = true;
                                    else if (option === 'Other' && approval && !matchesAnySpecific) matches = true;
                                    else if (approval) {
                                      if (option === 'Images Approved' && (effectiveImages === true || effectiveImages === 'true')) matches = true;
                                      else if (option === 'Images Auto' && effectiveImages === 'auto') matches = true;
                                      else if (option === 'Images Failed' && (effectiveImages === false || effectiveImages === 'false')) matches = true;
                                      else if (option === 'Words Approved' && (effectiveWords === true || effectiveWords === 'true')) matches = true;
                                      else if (option === 'Words Auto' && effectiveWords === 'auto') matches = true;
                                      else if (option === 'Words Failed' && (effectiveWords === false || effectiveWords === 'false')) matches = true;
                                      else if (option === 'Voice Added' && (approval.voice === true || approval.voice === 'true')) matches = true;
                                    }
                                  }
                                  
                                  if (matches) count++;
                                });
                                
                                return (
                                <label key={option} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', marginBottom: '4px' }}>
                                  <input
                                    type="checkbox"
                                    checked={activeFilters[header.column.id]?.includes(option) || false}
                                    onChange={() => toggleFilter(header.column.id, option)}
                                    style={{ margin: 0 }}
                                  />
                                    <span>{option || '(empty)'} ({count})</span>
                                </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                      </div>
                    </div>
                  </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => (
              <tr 
                key={row.id}
                style={{
                  backgroundColor: row.getIsSelected() ? '#e3f2fd' : undefined
                }}
              >
                {row.getVisibleCells().map(cell => (
                  <td 
                    key={cell.id}
                    data-column-id={cell.column.id}
                    style={{
                      backgroundColor: row.getIsSelected() ? '#e3f2fd' : undefined
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
      
      <div className="pagination-container">
        <div className="pagination-info">
          <span className="pagination-text">
            Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
            {Math.min(
              (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
              table.getFilteredRowModel().rows.length
            )}{' '}
            of {table.getFilteredRowModel().rows.length} results
          </span>
          <div className="items-per-page">
            <label htmlFor="items-per-page">Items per page:</label>
            <select
              id="items-per-page"
              value={table.getState().pagination.pageSize}
              onChange={(e) => {
                const newSize = Number(e.target.value);
                table.setPageSize(newSize);
                settingsService.setInfoTablePageSize(newSize);
                // Reset to first page when changing page size
                isUserNavigationRef.current = true;
                table.setPageIndex(0);
                settingsService.setInfoTablePageIndex(0);
              }}
              className="items-per-page-select"
            >
              <option value={1}>1</option>
              <option value={5}>5</option>
              <option value={6}>6</option>
              <option value={7}>7</option>
              <option value={8}>8</option>
              <option value={9}>9</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={20}>20</option>
              <option value={25}>25</option>
              <option value={30}>30</option>
              <option value={40}>40</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </select>
          </div>
        </div>
        
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => {
              console.log('[BookTable] First page button clicked, setting user navigation flag');
              isUserNavigationRef.current = true;
              table.setPageIndex(0);
              settingsService.setInfoTablePageIndex(0);
            }}
            disabled={!table.getCanPreviousPage()}
            aria-label="First page"
          >
            ««
          </button>
          
          <button
            className="pagination-btn"
            onClick={() => {
              const newIndex = table.getState().pagination.pageIndex - 1;
              isUserNavigationRef.current = true;
              table.setPageIndex(newIndex);
              settingsService.setInfoTablePageIndex(newIndex);
            }}
            disabled={!table.getCanPreviousPage()}
            aria-label="Previous page"
          >
            «
          </button>

          <span className="pagination-dots pagination-page-group">
            Page{' '}
            <input
              type="number"
              className="pagination-page-input"
              min={1}
              max={table.getPageCount()}
              value={pageInputValue || String(table.getState().pagination.pageIndex + 1)}
              onChange={(e) => setPageInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const pageCount = table.getPageCount();
                  const requested = parseInt(pageInputValue, 10);
                  if (!isNaN(requested) && pageCount >= 1) {
                    const page = Math.min(pageCount, Math.max(1, requested));
                    isUserNavigationRef.current = true;
                    table.setPageIndex(page - 1);
                    settingsService.setInfoTablePageIndex(page - 1);
                    setPageInputValue(String(page));
                  }
                }
              }}
              onBlur={() => setPageInputValue(String(table.getState().pagination.pageIndex + 1))}
              aria-label="Current page"
            />
            {' '}of {table.getPageCount()}
          </span>

          <button
            className="pagination-btn"
            onClick={() => {
              const newIndex = table.getState().pagination.pageIndex + 1;
              isUserNavigationRef.current = true;
              table.setPageIndex(newIndex);
              settingsService.setInfoTablePageIndex(newIndex);
            }}
            disabled={!table.getCanNextPage()}
            aria-label="Next page"
          >
            »
          </button>
          
          <button
            className="pagination-btn"
            onClick={() => {
              const lastIndex = table.getPageCount() - 1;
              isUserNavigationRef.current = true;
              table.setPageIndex(lastIndex);
              settingsService.setInfoTablePageIndex(lastIndex);
            }}
            disabled={!table.getCanNextPage()}
            aria-label="Last page"
          >
            »»
          </button>
        </div>
      </div>
    </div>

    {dbSyncModal && (
      <div
        className="modal-overlay"
        onClick={() => setDbSyncModal(null)}
        style={{
          alignItems: 'flex-start',
          justifyContent: 'center',
          paddingTop: 'min(2rem, 5vh)',
          paddingBottom: 'min(2rem, 5vh)',
          overflowY: 'auto',
        }}
      >
        <div
          className="modal-content"
          onClick={e => e.stopPropagation()}
          style={{
            maxWidth: '560px',
            minWidth: '400px',
            maxHeight: 'calc(100vh - 4rem)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.125rem', flexShrink: 0 }}>Sync to Admin Tool</h3>
          <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.875rem', color: 'var(--text-secondary)', flexShrink: 0 }}>
            This will send the following update to the admin tool (DB). Confirm to proceed.
          </p>
          {dbSyncModal.ineligibleList && dbSyncModal.ineligibleList.length > 0 && (
            <div
              style={{
                marginBottom: '0.75rem',
                fontSize: '0.875rem',
                color: 'var(--text-secondary)',
                flex: '1 1 0',
                minHeight: 0,
                overflowY: 'auto',
                padding: '0.5rem 0',
              }}
            >
              <p style={{ margin: 0 }}>
                The following are not eligible and will be skipped (must be in current list, in all-books, and have images created):{' '}
                {dbSyncModal.ineligibleList.map((x) => (x.title ? `${x.title} (${x.isbn})` : x.isbn)).join(', ')}
              </p>
            </div>
          )}
          <pre
            style={{
              margin: '0 0 1rem 0',
              padding: '0.75rem',
              fontSize: '0.8125rem',
              fontFamily: 'monospace',
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-primary)',
              borderRadius: '6px',
              overflow: 'auto',
              maxHeight: '240px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              flexShrink: 0,
            }}
          >
            {JSON.stringify(dbSyncModal.payload, null, 2)}
          </pre>
          <div className="modal-actions" style={{ flexShrink: 0 }}>
            <button
              className="modal-button modal-button-primary"
              onClick={() => handleConfirmDbSync()}
            >
              Confirm
            </button>
            <button
              className="modal-button modal-button-secondary"
              onClick={() => setDbSyncModal(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}

    {showCopyIsbnsModal && (
      <div className="modal-overlay" onClick={() => setShowCopyIsbnsModal(false)}>
        <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px', minWidth: '400px' }}>
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.125rem' }}>Copy All ISBNs</h3>
          <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
            {filteredBooks.length} ISBN{filteredBooks.length !== 1 ? 's' : ''} in current filter/search
          </p>
          <textarea
            readOnly
            value={copyIsbnsText}
            style={{
              width: '100%',
              minHeight: '300px',
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              padding: '0.75rem',
              borderRadius: '6px',
              border: '1px solid var(--border-primary)',
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              resize: 'vertical',
            }}
            onClick={(e) => (e.target as HTMLTextAreaElement).select()}
          />
          <div className="modal-actions">
            <button
              className="modal-button modal-button-primary"
              onClick={() => {
                navigator.clipboard.writeText(copyIsbnsText);
                setShowCopyIsbnsModal(false);
              }}
            >
              Copy & Close
            </button>
            <button
              className="modal-button modal-button-secondary"
              onClick={() => setShowCopyIsbnsModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default BookTable;
